import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { useOnTimeLiveData } from '../hooks/useOnTimeLiveData';
import { formatMoney, asDate } from '../lib/formatters';
import { getActiveStoreNames } from '../lib/ontimeSettings';
import { isDemoOrGhostOrder } from '../lib/liveOrderFilters';
import {
  controlTowerApproveAdjustment,
  controlTowerSendToDifferentStore,
  controlTowerCancelOrder,
  linkOrderSendMessage,
} from '../lib/actions';

const FALLBACK_STORES = ['District Six', 'Parow'];
const TABS = ['Overview', 'Timeline', 'Chat', 'Evidence', 'Driver'];

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function firstText(...values) {
  return values.map((value) => String(value || '').trim()).find(Boolean) || '';
}

function money(value) {
  return formatMoney(Number(value || 0));
}

function getMs(value) {
  const date = asDate(value);
  return date?.getTime?.() || 0;
}

function minutesSince(value, now) {
  const ms = getMs(value);
  if (!ms) return 0;
  return Math.max(0, Math.floor((now - ms) / 60000));
}

function fmtMins(mins) {
  const safe = Math.max(0, Number(mins || 0));
  const h = Math.floor(safe / 60);
  const m = safe % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
}

function formatClock(value) {
  const date = asDate(value);
  if (!date) return '—';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatSearchDate(value) {
  const date = asDate(value);
  if (!date) return 'No date';
  const today = new Date();
  const isToday = date.toDateString() === today.toDateString();
  if (isToday) return 'Today';
  return date.toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: '2-digit' });
}

function orderSearchDate(order = {}) {
  const raw = order.raw || {};
  return order.archivedAt || raw.archivedAt || order.timings?.deliveredAt || raw.timings?.deliveredAt || order.timings?.completedAt || raw.timings?.completedAt || order.timings?.cancelledAt || raw.timings?.cancelledAt || order.createdAt || raw.createdAt;
}

function itemName(item = {}) {
  return firstText(item.name, item.productName, item.title, item.sku, 'Item');
}

function itemQty(item = {}) {
  return Number(item.qty ?? item.quantity ?? item.count ?? 1) || 1;
}

function itemKey(item = {}, index = 0) {
  return String(item.id || item.lineId || item.productId || item.sku || itemName(item) || index);
}

function itemPrice(item = {}) {
  const direct = Number(item.lineTotal ?? item.total ?? item.amount ?? 0);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const unit = Number(item.price ?? item.unitPrice ?? item.sellPrice ?? item.value ?? 0) || 0;
  return unit * itemQty(item);
}

function isAdditionItem(item = {}) {
  const id = String(item.id || item.lineId || item.productId || '').toLowerCase();
  const status = String(item.status || item.itemStatus || item.type || '').toLowerCase();
  return item.isSwap === true || item.isAddition === true || item.addedBySite || item.addedAtLabel || item.swapFor || id.startsWith('swap_') || status.includes('addition') || status.includes('swap');
}

function originalItems(order = {}) {
  const raw = order.raw || {};
  const directOriginal = safeArray(order.originalOrderItems).length ? order.originalOrderItems : safeArray(raw.originalOrderItems);
  if (directOriginal.length) return directOriginal.filter((item) => !isAdditionItem(item));
  const source = safeArray(raw.items).length ? raw.items : safeArray(order.items);
  return source.filter((item) => !isAdditionItem(item));
}

function additionItems(order = {}) {
  const raw = order.raw || {};
  const additions = [...safeArray(order.additions), ...safeArray(raw.additions)];
  const items = safeArray(raw.items).length ? raw.items : safeArray(order.items);
  const fromItems = items.filter(isAdditionItem);
  const all = [...additions, ...fromItems];
  return all.filter((item, index, arr) => arr.findIndex((row) => itemKey(row, index) === itemKey(item, index)) === index);
}

function unavailableSet(order = {}) {
  const raw = order.raw || {};
  const flags = order.flags || raw.flags || {};
  const map = flags.unavailableMap || raw.unavailableMap || {};
  const values = [
    ...Object.keys(map).filter((key) => map[key]),
    ...safeArray(order.unavailableItems),
    ...safeArray(raw.unavailableItems),
    ...safeArray(order.unavailableItemNames),
    ...safeArray(raw.unavailableItemNames),
  ];
  return new Set(values.map((value) => String(value).toLowerCase()));
}

function isUnavailable(order, item, index) {
  const set = unavailableSet(order);
  const key = itemKey(item, index).toLowerCase();
  const name = itemName(item).toLowerCase();
  const status = String(item.status || item.itemStatus || '').toLowerCase();
  return set.has(key) || set.has(name) || status.includes('unavailable');
}

function deliveryFee(order = {}) {
  const raw = order.raw || {};
  return Number(raw.pricing?.deliveryFee ?? order.deliveryFee ?? raw.deliveryFee ?? raw.delivery ?? 0) || 0;
}

function originalPaidTotal(order = {}) {
  const raw = order.raw || {};
  const itemsTotal = originalItems(order).reduce((sum, item) => sum + itemPrice(item), 0);
  const delivery = deliveryFee(order);
  const explicit = Number(
    raw.originalPaidTotal ??
    raw.originalOrderTotal ??
    raw.pricing?.originalTotal ??
    raw.pricing?.total ??
    order.originalPaidTotal ??
    order.originalOrderTotal ??
    order.total ??
    0
  );
  return explicit > 0 ? explicit : itemsTotal + delivery;
}

function livePackSubtotal(order = {}) {
  const originals = originalItems(order).filter((item, index) => !isUnavailable(order, item, index));
  const additions = additionItems(order);
  return originals.reduce((sum, item) => sum + itemPrice(item), 0) + additions.reduce((sum, item) => sum + itemPrice(item), 0);
}

function liveOrderTotal(order = {}) {
  return livePackSubtotal(order) + deliveryFee(order);
}

function paymentLabel(order = {}) {
  const raw = order.raw || {};
  return firstText(raw.payment?.label, raw.payment?.method, order.paymentMethod, raw.paymentMethod, 'Unknown');
}

function isCardPayment(order = {}) {
  return /card|paid|online/i.test(paymentLabel(order));
}

function customerName(order = {}) {
  const raw = order.raw || {};
  return firstText(order.customerName, raw.customer?.name, raw.customerName, 'Customer');
}

function customerPhone(order = {}) {
  const raw = order.raw || {};
  return firstText(raw.customer?.phone, order.customerPhone, raw.customerPhone);
}

function customerAddress(order = {}) {
  const raw = order.raw || {};
  return firstText(order.address, raw.customer?.addressLine1, raw.address, raw.deliveryAddress);
}


function isArchivedOrReroutedOrder(order = {}) {
  const raw = order.raw || {};
  const stage = String(order.stage || raw.stage || '').toLowerCase();
  const status = String(order.status || raw.status || '').toLowerCase();
  return Boolean(
    order.archived ||
    raw.archived ||
    order.archivedAt ||
    raw.archivedAt ||
    order.reroutedToOrderId ||
    raw.reroutedToOrderId ||
    order.archiveCategory === 'rerouted' ||
    raw.archiveCategory === 'rerouted' ||
    ['archived', 'cancelled', 'delivered'].includes(stage) ||
    ['archived', 'cancelled', 'delivered'].includes(status)
  );
}

function orderStore(order = {}) {
  return firstText(order.store, order.raw?.storeName, order.raw?.store, 'Unassigned store');
}

function sentBy(order = {}) {
  return firstText(order.sentByName, order.createdByName, order.createdByDisplayName, order.raw?.createdBy?.name, order.assignedOnline, 'Online');
}

function packerName(order = {}) {
  const proof = order.storeProof || order.proof || order.raw?.storeProof || order.raw?.proof || {};
  return firstText(order.packerName, order.raw?.packerName, order.assignedStoreUserName, proof.packedBy, order.assignedStoreUser, '—');
}

function driverName(order = {}) {
  if (!['ready_for_driver', 'out_for_delivery', 'delivered'].includes(order.stage)) return 'Not selected';
  return firstText(order.assignedDriverName, order.assignedDriver, order.assignment?.driverName, order.assignment?.driverId, order.raw?.assignment?.driverName, order.raw?.assignment?.driverId, 'Not selected');
}

function stageLabel(order = {}) {
  const stage = String(order.stage || '').replace(/_/g, ' ');
  if (order.swapApproved) return 'Approved';
  if (order.issueFlag || order.flags?.hasStockIssue) return 'Needs approval';
  return stage || 'Waiting';
}

function currentOperationalWait(order = {}) {
  const stage = String(order.stage || '').toLowerCase();
  const storeStatus = String(order.storeStatus || order.fulfilment?.storeStatus || '').toLowerCase();
  if (stage === 'out_for_delivery') return 'Driver to deliver';
  if (stage === 'delivered') return 'Delivered';
  if (storeStatus === 'collected') return 'Driver to start run';
  if (stage === 'ready_for_driver') return 'Driver collection';
  if (storeStatus === 'packing' || stage === 'packing') return 'Store packing';
  if (approvalRequired(order)) return 'Online approval';
  return stageLabel(order);
}

function approvalRequired(order = {}) {
  return (originalItems(order).some((item, idx) => isUnavailable(order, item, idx)) || additionItems(order).length > 0) && !order.swapApproved;
}

function timerSource(order = {}) {
  const timings = order.timings || {};
  if (approvalRequired(order)) return timings.swapApprovalStartedAt || timings.unavailableMarkedAt || timings.sentToStoreAt || order.createdAt;
  if (order.stage === 'ready_for_driver') return timings.readyForDriverAt || timings.packedAt || order.updatedAt;
  if (order.stage === 'out_for_delivery') return timings.outForDeliveryAt || timings.collectedAt || order.updatedAt;
  return timings.sentToStoreAt || timings.storeAcceptedAt || order.createdAt;
}

function orderDelay(order, now) {
  return fmtMins(minutesSince(timerSource(order), now));
}

function orderAgeSource(order = {}) {
  const timings = { ...(order.raw?.timings || {}), ...(order.timings || {}) };
  return (
    order.overallStartedAt ||
    order.originalCreatedAt ||
    order.placedAt ||
    timings.orderCreatedAt ||
    timings.createdAt ||
    timings.sentToStoreAt ||
    order.createdAt ||
    order.raw?.createdAt
  );
}

function overallOrderAgeMinutes(order = {}, now = Date.now()) {
  return minutesSince(orderAgeSource(order), now);
}

function overallSlaStatus(order = {}, now = Date.now()) {
  if (isArchivedOrReroutedOrder(order) || isDeliveredLike(order)) {
    return { severity: 'healthy', label: 'Complete', minutes: overallOrderAgeMinutes(order, now), tone: 'healthy' };
  }
  const minutes = overallOrderAgeMinutes(order, now);
  if (minutes >= 45) return { severity: 'critical', label: 'Critical delay', minutes, tone: 'critical' };
  if (minutes >= 30) return { severity: 'delayed', label: 'At risk delay', minutes, tone: 'delayed' };
  if (minutes >= 15) return { severity: 'operational', label: 'Attention', minutes, tone: 'operational' };
  return { severity: 'healthy', label: 'Healthy', minutes, tone: 'healthy' };
}

function orderDurations(order, now) {
  const t = { ...(order.raw?.timings || {}), ...(order.timings || {}) };
  const start = getMs(order.overallStartedAt || order.originalCreatedAt || order.placedAt || order.createdAt || order.raw?.createdAt);
  const sent = getMs(t.sentToStoreAt || order.sentToStoreAt || order.placedAt || order.createdAt || order.raw?.createdAt);
  const packStart = getMs(t.packingStartedAt || t.storeAcceptedAt);
  const ready = getMs(t.readyForDriverAt || t.packedAt);
  const collected = getMs(t.collectedAt);
  const out = getMs(t.outForDeliveryAt || t.driverAcceptedAt || t.driverStartedAt);
  const delivered = getMs(t.deliveredAt || t.completedAt || t.driverDeliveredAt);
  const end = delivered || now;
  return {
    waiting: sent ? fmtMins(Math.floor(((packStart || ready || collected || out || delivered || now) - sent) / 60000)) : '—',
    packing: packStart ? fmtMins(Math.floor(((ready || collected || out || delivered || now) - packStart) / 60000)) : '—',
    ready: ready ? fmtMins(Math.floor(((collected || out || delivered || now) - ready) / 60000)) : '—',
    collected: (collected || out) ? fmtMins(Math.floor(((delivered || now) - (out || collected)) / 60000)) : '—',
    overall: start ? fmtMins(Math.floor((end - start) / 60000)) : '—',
  };
}

function timelineRows(order, now) {
  const embedded = safeArray(order.timeline).map((event) => ({
    title: firstText(event.title, event.text, event.message, event.type, 'Operational event'),
    at: event.at || event.createdAt || event.clientAt,
    photoUrl: event.photoUrl || event.attachmentUrl || '',
  }));
  const live = safeArray(order.events).map((event) => ({
    title: firstText(event.title, event.message, event.type, 'Operational event'),
    at: event.createdAt || event.clientAt || event.at,
    photoUrl: event.photoUrl || event.attachmentUrl || '',
  }));
  const rows = [...embedded, ...live].filter((event, index, arr) => {
    const key = `${event.title}-${getMs(event.at)}`;
    return arr.findIndex((row) => `${row.title}-${getMs(row.at)}` === key) === index;
  });
  rows.sort((a, b) => getMs(b.at) - getMs(a.at));
  if (rows.length) {
    return rows.map((row, index) => {
      const current = getMs(row.at);
      const next = getMs(rows[index - 1]?.at) || now;
      const durationMins = current ? Math.max(0, Math.floor((next - current) / 60000)) : 0;
      let intelligence = '';
      let tone = 'normal';

      if (/packing/i.test(row.title) && durationMins >= 15) {
        intelligence = `Packing exceeded average by ${fmtMins(durationMins - 10)}.`;
        tone = 'delayed';
      } else if (/driver accepted/i.test(row.title) && durationMins >= 10) {
        intelligence = `Driver accepted after ${fmtMins(durationMins)}.`;
        tone = 'critical';
      } else if (/out for delivery|collected/i.test(row.title) && durationMins >= 25) {
        intelligence = `Delivery route delayed by ${fmtMins(durationMins)}.`;
        tone = 'critical';
      }

      return {
        ...row,
        duration: current ? fmtMins(durationMins) : '—',
        intelligence,
        tone,
      };
    });
  }
  return [
    { title: 'Order received', at: order.createdAt, duration: fmtMins(minutesSince(order.createdAt, now)) },
  ];
}


function timelineStageIntelligence(order = {}, now = Date.now()) {
  const timings = { ...(order.raw?.timings || {}), ...(order.timings || {}) };
  const stage = String(order.stage || order.raw?.stage || '').toLowerCase();
  const storeStatus = String(order.storeStatus || order.fulfilment?.storeStatus || order.raw?.storeStatus || '').toLowerCase();

  const stageConfig = {
    approval: { label: 'Online approval', detail: 'Review totals or customer replacement', sla: 10, toneAt: 20, source: timings.swapApprovalStartedAt || timings.unavailableMarkedAt || timings.sentToStoreAt || order.createdAt },
    waiting: { label: 'Store acceptance', detail: 'Waiting for site action', sla: 8, toneAt: 16, source: timings.sentToStoreAt || timings.storeQueuedAt || order.createdAt || order.placedAt },
    packing: { label: 'Store packing', detail: 'Packing and proof capture', sla: 15, toneAt: 25, source: timings.packingStartedAt || timings.storeAcceptedAt || timings.sentToStoreAt || order.updatedAt },
    ready: { label: 'Driver selection', detail: 'Select or request driver', sla: 10, toneAt: 20, source: timings.readyForDriverAt || timings.packedAt || order.updatedAt },
    collected: { label: 'Handover / start run', detail: 'Driver has collected from site', sla: 10, toneAt: 20, source: timings.collectedAt || timings.outForDeliveryAt || order.updatedAt },
    delivery: { label: 'Delivery run', detail: 'Driver en route to customer', sla: 35, toneAt: 50, source: timings.outForDeliveryAt || timings.driverStartedAt || timings.driverAcceptedAt || timings.collectedAt || order.updatedAt },
    delivered: { label: 'Delivered', detail: 'Order completed', sla: 0, toneAt: 0, source: timings.deliveredAt || timings.completedAt || timings.driverDeliveredAt || order.updatedAt },
  };

  let key = 'waiting';
  if (approvalRequired(order)) key = 'approval';
  else if (stage === 'packing' || storeStatus === 'packing') key = 'packing';
  else if (stage === 'ready_for_driver') key = 'ready';
  else if (storeStatus === 'collected') key = 'collected';
  else if (stage === 'out_for_delivery') key = 'delivery';
  else if (stage === 'delivered') key = 'delivered';
  else if (['awaiting_store', 'new', 'waiting'].includes(stage)) key = 'waiting';

  const cfg = stageConfig[key];
  const mins = key === 'delivered' ? 0 : Math.max(0, minutesSince(cfg.source, now));
  const breach = Math.max(0, mins - cfg.sla);
  const overallStatus = overallSlaStatus(order, now);
  const tone = key === 'delivered'
    ? 'healthy'
    : overallStatus.severity === 'critical'
      ? 'critical'
      : overallStatus.severity === 'delayed'
        ? 'delayed'
        : mins >= cfg.toneAt
          ? 'critical'
          : breach > 0
            ? 'delayed'
            : 'healthy';

  return {
    key,
    label: cfg.label,
    detail: cfg.detail,
    minutes: mins,
    time: key === 'delivered' ? 'Complete' : fmtMins(mins),
    sla: cfg.sla,
    breach,
    breachText: breach > 0 ? `Breach +${fmtMins(breach)}` : 'Within SLA',
    tone,
    source: cfg.source,
  };
}

function timelineFlowRows(order = {}, now = Date.now()) {
  const timings = { ...(order.raw?.timings || {}), ...(order.timings || {}) };
  const current = timelineStageIntelligence(order, now);
  const rows = [
    { key: 'created', label: 'Order created', at: order.createdAt || order.placedAt || order.raw?.createdAt },
    { key: 'approval', label: approvalRequired(order) ? 'Approval pending' : 'Approval clear', at: timings.swapApprovedAt || timings.unavailableApprovedAt || timings.sentToStoreAt || order.createdAt },
    { key: 'waiting', label: 'Store acceptance', at: timings.storeAcceptedAt || timings.packingStartedAt },
    { key: 'packing', label: 'Packing', at: timings.packingStartedAt || timings.storeAcceptedAt },
    { key: 'ready', label: 'Ready for driver', at: timings.readyForDriverAt || timings.packedAt },
    { key: 'collected', label: 'Collected', at: timings.collectedAt },
    { key: 'delivery', label: 'Out for delivery', at: timings.outForDeliveryAt || timings.driverStartedAt || timings.driverAcceptedAt },
    { key: 'delivered', label: 'Delivered', at: timings.deliveredAt || timings.completedAt || timings.driverDeliveredAt },
  ];

  return rows.map((row) => {
    const done = Boolean(getMs(row.at));
    const active = row.key === current.key;
    const tone = active ? current.tone : done ? 'healthy' : 'pending';
    return {
      ...row,
      done,
      active,
      tone,
      time: done ? formatClock(row.at) : active ? current.time : '—',
      note: active ? `${current.detail} · ${current.breachText}` : done ? 'Completed' : 'Pending',
    };
  });
}

function chatMessages(order = {}) {
  const raw = order.raw || {};
  const rows = [...safeArray(raw.linkChatMessages), ...safeArray(order.linkChatMessages)];
  return rows
    .filter(Boolean)
    .filter((msg, idx, arr) => arr.findIndex((m) => String(m.id || m.createdAt || m.text) === String(msg.id || msg.createdAt || msg.text)) === idx)
    .sort((a, b) => getMs(a.createdAt) - getMs(b.createdAt));
}

function evidenceCards(order = {}) {
  const proof = order.proof || order.raw?.proof || {};
  const storeProof = order.storeProof || order.raw?.storeProof || {};
  const driverProof = order.raw?.driverProof || order.driverProof || {};
  return [
    { title: 'Packed', time: storeProof.packedAt || order.timings?.packedAt, url: proof.packedPhotoUrl || storeProof.packedPhotoUrl },
    { title: 'Handover', time: storeProof.collectedAt || order.timings?.collectedAt, url: proof.collectedPhotoUrl || storeProof.collectedPhotoUrl },
    { title: 'Collected', time: driverProof.pickupAt || driverProof.acceptedAt || order.timings?.driverPickedUpAt || order.timings?.outForDeliveryAt || order.timings?.driverAcceptedAt, url: proof.driverPickupPhotoUrl || proof.driverStartedPhotoUrl || proof.driverAcceptedPhotoUrl || driverProof.pickupPhotoUrl || driverProof.startedPhotoUrl || driverProof.acceptedPhotoUrl },
    { title: 'Delivered', time: driverProof.deliveredAt || proof.deliveredAt || order.timings?.deliveredAt, url: proof.deliveredPhotoUrl || proof.photoUrl || driverProof.deliveredPhotoUrl },
  ];
}


function storeDistanceEstimate(store = '') {
  const estimates = {
    'District Six': { distance: '2.4 km', eta: '7 min' },
    Parow: { distance: '18.6 km', eta: '28 min' },
  };
  return estimates[store] || { distance: '—', eta: '—' };
}



function orderRouteEtaMinutes(order = {}) {
  const raw = order.raw || {};
  const routing = order.routing || raw.routing || {};
  const direct = Number(order.etaMinutes || raw.etaMinutes || routing.etaMinutes || routing.routeEtaMinutes);
  if (Number.isFinite(direct) && direct > 0) return Math.round(direct);
  const km = Number(routing.distanceKm || order.distanceKm || raw.distanceKm || routing.routeDistanceKm);
  if (Number.isFinite(km) && km > 0) return Math.max(5, Math.round(km * 1.7));
  return null;
}

function evidenceCardsForAttention(order = {}) {
  return evidenceCards(order);
}

function hasDeliveryProof(order = {}) {
  const proof = order.proof || order.raw?.proof || {};
  const driverProof = order.driverProof || order.raw?.driverProof || {};
  return Boolean(
    proof.deliveredPhotoUrl ||
    proof.deliveryPhotoUrl ||
    proof.driverDeliveryPhotoUrl ||
    driverProof.deliveredPhotoUrl ||
    driverProof.deliveryPhotoUrl ||
    driverProof.photoUrl ||
    order.deliveredPhotoUrl ||
    order.deliveryPhotoUrl
  );
}

function driverIsOnline(order = {}, drivers = []) {
  const assigned = driverName(order);
  if (!assigned || assigned === 'Not selected') return true;
  const row = drivers.find((driver) => {
    const values = [driver.id, driver.name, driver.fullName, driver.displayName, driver.email]
      .map((value) => String(value || '').toLowerCase());
    return values.includes(String(assigned || '').toLowerCase());
  });
  if (!row) return true;
  const status = String(row.status || row.state || '').toLowerCase();
  return row.online === true || status === 'online' || status === 'available' || status === 'busy' || status === 'on_run';
}

function attentionStageMinutes(order = {}, now = Date.now()) {
  const timings = order.timings || {};
  const stage = String(order.stage || '').toLowerCase();

  if (approvalRequired(order)) {
    return minutesSince(timings.swapApprovalStartedAt || timings.unavailableMarkedAt || timings.sentToStoreAt || order.createdAt, now);
  }

  if (stage === 'awaiting_store' || stage === 'new' || stage === 'waiting') {
    return minutesSince(timings.sentToStoreAt || timings.storeQueuedAt || order.createdAt || order.placedAt, now);
  }

  if (stage === 'packing') {
    return minutesSince(timings.packingStartedAt || timings.storeAcceptedAt || timings.sentToStoreAt || order.updatedAt, now);
  }

  if (stage === 'ready_for_driver') {
    return minutesSince(timings.readyForDriverAt || timings.packedAt || order.updatedAt, now);
  }

  if (stage === 'out_for_delivery') {
    return minutesSince(timings.outForDeliveryAt || timings.collectedAt || timings.driverPickedUpAt || order.updatedAt, now);
  }

  return minutesSince(timerSource(order), now);
}

function codMismatch(order = {}) {
  const raw = order.raw || {};
  const expected = Number(order.expectedCOD ?? raw.expectedCOD ?? order.codExpected ?? raw.codExpected ?? 0);
  const collected = Number(order.collectedCOD ?? raw.collectedCOD ?? order.codCollected ?? raw.codCollected ?? 0);
  return expected > 0 && collected > 0 && Math.abs(expected - collected) > 0.01;
}

function orderBalance(order = {}) {
  return liveOrderTotal(order) - originalPaidTotal(order);
}

function orderHasAssignedDriver(order = {}) {
  const assigned = driverName(order);
  return Boolean(assigned && assigned !== 'Not selected');
}

function proofMissingForStage(order = {}, stage = '') {
  const proof = order.proof || order.raw?.proof || {};
  const storeProof = order.storeProof || order.raw?.storeProof || {};
  const driverProof = order.driverProof || order.raw?.driverProof || {};
  if (stage === 'ready_for_driver' || stage === 'out_for_delivery') {
    return !Boolean(proof.packedPhotoUrl || storeProof.packedPhotoUrl || proof.collectedPhotoUrl || storeProof.collectedPhotoUrl);
  }
  if (stage === 'delivered' || stage === 'completed') return !hasDeliveryProof(order);
  return false;
}

function buildAttentionItems(orders = [], drivers = [], now = Date.now()) {
  const thresholds = {
    waiting: 5,
    packing: 8,
    ready: 5,
    delivery: 25,
  };

  const items = [];

  orders
    .filter((order) => !isArchivedOrReroutedOrder(order))
    .forEach((order) => {
      const stage = String(order.stage || '').toLowerCase();
      const stageMins = attentionStageMinutes(order, now);
      const overallMins = overallOrderAgeMinutes(order, now);
      const slaStatus = overallSlaStatus(order, now);
      const base = {
        id: order.id,
        order,
        orderNumber: order.orderNumber || order.id,
        site: orderStore(order),
        customer: customerName(order),
        driver: driverName(order),
        timer: fmtMins(overallMins),
        stageTimer: fmtMins(stageMins),
        overallMins,
      };

      if (slaStatus.severity === 'critical' || slaStatus.severity === 'delayed') {
        items.push({
          ...base,
          key: `${order.id}-overall-sla`,
          severity: slaStatus.severity,
          type: slaStatus.label,
          reason: `Order has been in fulfilment for ${fmtMins(overallMins)}. Current stage: ${stageLabel(order)} for ${fmtMins(stageMins)}.`,
          action: slaStatus.severity === 'critical' ? 'Open intervention' : 'Monitor order',
          score: (slaStatus.severity === 'critical' ? 9000 : 6500) + overallMins,
        });
      }

      if (approvalRequired(order) && stageMins >= thresholds.waiting) {
        items.push({
          ...base,
          key: `${order.id}-approval-delay`,
          severity: 'critical',
          type: 'Approval delay',
          reason: `Swap/removal has waited ${fmtMins(stageMins)} for approval.`,
          action: 'Open order approval',
          score: 3000 + stageMins,
        });
      }

      if (['new', 'waiting', 'awaiting_store'].includes(stage) && stageMins >= thresholds.waiting) {
        items.push({
          ...base,
          key: `${order.id}-waiting-delay`,
          severity: stageMins >= thresholds.waiting * 2 ? 'critical' : 'delayed',
          type: 'Waiting delay',
          reason: `Order has waited ${fmtMins(stageMins)} before store action.`,
          action: 'Message site',
          score: (stageMins >= thresholds.waiting * 2 ? 3000 : 2000) + stageMins,
        });
      }

      if (stage === 'packing' && stageMins >= thresholds.packing) {
        items.push({
          ...base,
          key: `${order.id}-packing-delay`,
          severity: stageMins >= thresholds.packing * 2 ? 'critical' : 'delayed',
          type: 'Packing delay',
          reason: `Packing has been active for ${fmtMins(stageMins)}.`,
          action: 'Check packer',
          score: (stageMins >= thresholds.packing * 2 ? 3000 : 2000) + stageMins,
        });
      }

      if (stage === 'ready_for_driver' && stageMins >= thresholds.ready) {
        items.push({
          ...base,
          key: `${order.id}-ready-delay`,
          severity: 'critical',
          type: 'Ready not collected',
          reason: `Ready order has waited ${fmtMins(stageMins)} for driver collection.`,
          action: 'Assign / reassign driver',
          score: 3000 + stageMins,
        });
      }

      if (stage === 'out_for_delivery' && !driverIsOnline(order, drivers)) {
        items.push({
          ...base,
          key: `${order.id}-driver-offline`,
          severity: 'critical',
          type: 'Driver offline',
          reason: 'Driver appears offline while carrying an active run.',
          action: 'Contact driver',
          score: 4000 + stageMins,
        });
      }

      if (stage === 'out_for_delivery' && stageMins >= thresholds.delivery) {
        items.push({
          ...base,
          key: `${order.id}-delivery-delay`,
          severity: stageMins >= thresholds.delivery * 2 ? 'critical' : 'delayed',
          type: 'Delivery delay',
          reason: `Driver has been on route for ${fmtMins(stageMins)}.`,
          action: 'Open driver panel',
          score: (stageMins >= thresholds.delivery * 2 ? 3000 : 2000) + stageMins,
        });
      }

      if (stage === 'ready_for_driver' && !orderHasAssignedDriver(order)) {
        items.push({
          ...base,
          key: `${order.id}-no-driver`,
          severity: stageMins >= thresholds.ready ? 'critical' : 'operational',
          type: 'No driver selected',
          reason: stageMins >= thresholds.ready
            ? `Ready order has no driver after ${fmtMins(stageMins)}.`
            : 'Ready order is waiting for a driver selection.',
          action: 'Assign driver',
          score: (stageMins >= thresholds.ready ? 3200 : 1200) + stageMins,
        });
      }

      if (proofMissingForStage(order, stage)) {
        items.push({
          ...base,
          key: `${order.id}-stage-proof-gap`,
          severity: stage === 'out_for_delivery' ? 'critical' : 'operational',
          type: 'Proof gap',
          reason: 'This order has moved forward while one expected proof photo is missing.',
          action: 'Open evidence',
          score: (stage === 'out_for_delivery' ? 3400 : 1400) + stageMins,
        });
      }

      const balance = orderBalance(order);
      if (Math.abs(balance) > 0.01 && (approvalRequired(order) || order.swapApproved || additionItems(order).length)) {
        items.push({
          ...base,
          key: `${order.id}-balance-risk`,
          severity: balance > 0 ? 'operational' : 'critical',
          type: balance > 0 ? 'Customer owes balance' : 'Customer refund due',
          reason: balance > 0
            ? `Customer owes ${money(Math.abs(balance))} after adjustments.`
            : `Store owes customer ${money(Math.abs(balance))} after adjustments.`,
          action: 'Review totals',
          score: (balance > 0 ? 1500 : 3100) + stageMins,
        });
      }

      if (['delivered', 'completed'].includes(stage) && !hasDeliveryProof(order)) {
        items.push({
          ...base,
          key: `${order.id}-missing-proof`,
          severity: 'critical',
          type: 'Missing proof',
          reason: 'Order is completed without delivery proof attached.',
          action: 'Open evidence',
          score: 3500,
        });
      }

      if (codMismatch(order)) {
        items.push({
          ...base,
          key: `${order.id}-cod-mismatch`,
          severity: 'critical',
          type: 'COD mismatch',
          reason: 'Expected cash and collected cash do not match.',
          action: 'Review payment',
          score: 3600,
        });
      }

      if (order.reroutedFromOrderId || order.reroutedToOrderId || order.reroutedToStore) {
        items.push({
          ...base,
          key: `${order.id}-reroute`,
          severity: 'operational',
          type: 'Rerouted order',
          reason: 'Order has been rerouted and should remain visible in audit.',
          action: 'Open timeline',
          score: 1000 + stageMins,
        });
      }

      if (order.swapApproved || additionItems(order).length || originalItems(order).some((item, index) => isUnavailable(order, item, index))) {
        items.push({
          ...base,
          key: `${order.id}-adjustment`,
          severity: 'operational',
          type: 'Order adjustment',
          reason: 'Order contains stock adjustment, swap, or removal activity.',
          action: 'Review totals',
          score: 1000 + stageMins,
        });
      }
    });

  const unique = [];
  const seenOrders = new Set();
  items
    .sort((a, b) => b.score - a.score)
    .forEach((item) => {
      const orderKey = item.order?.id || item.id || item.key;
      if (seenOrders.has(orderKey)) return;
      seenOrders.add(orderKey);
      unique.push(item);
    });

  return unique;
}

function attentionCounts(items = []) {
  return {
    critical: items.filter((item) => item.severity === 'critical').length,
    delayed: items.filter((item) => item.severity === 'delayed').length,
    operational: items.filter((item) => item.severity === 'operational').length,
  };
}



function driverDisplayName(row = {}) {
  return firstText(row.name, row.fullName, row.displayName, row.email, row.id, 'Unknown driver');
}

function isGenericDriverName(value = '') {
  const text = String(value || '').trim();
  if (!text) return true;
  const lower = text.toLowerCase();
  return (
    lower === 'unknown driver' ||
    lower === 'not selected' ||
    lower.startsWith('link_') ||
    lower.startsWith('driver_') ||
    lower.startsWith('profile_') ||
    /^[a-z0-9_-]{18,}$/i.test(text)
  );
}

function cleanDriverName(row = {}, fallback = 'Driver profile') {
  const name = firstText(row.name, row.fullName, row.displayName, row.driverName);
  if (name && !isGenericDriverName(name)) return name;
  const email = firstText(row.email);
  if (email && !isGenericDriverName(email)) return email;
  const id = firstText(row.id, row.uid);
  return isGenericDriverName(id) ? fallback : id;
}

function driverAssignedSite(driver = {}, assignedOrders = [], liveAssigned = []) {
  const raw = driver.raw || driver || {};
  const order = liveAssigned[0] || assignedOrders[0];
  const orderSite = order ? orderStore(order) : '';
  const direct = firstText(raw.siteName, raw.assignedSite, raw.site, raw.storeName, raw.store);
  if (direct && !String(direct).toLowerCase().includes('all') && FALLBACK_STORES.includes(direct)) return direct;
  if (orderSite) return orderSite;
  return 'No site assigned';
}

function driverMatchTokens(value = '') {
  const text = String(value || '').toLowerCase().trim();
  return text ? [text, text.replace(/\s+/g, ''), text.replace(/[^a-z0-9]+/g, '')] : [];
}

function driverMatchesOrder(order = {}, driver = {}) {
  const assigned = driverName(order);
  if (!assigned || assigned === 'Not selected') return false;
  const assignedTokens = new Set(driverMatchTokens(assigned));
  const values = [
    driver.id,
    driver.name,
    driver.fullName,
    driver.displayName,
    driver.email,
    driver.phone,
  ].flatMap(driverMatchTokens);
  return values.some((value) => assignedTokens.has(value));
}

function driverOnlineStatus(driver = {}) {
  const status = String(driver.status || driver.state || '').toLowerCase();
  if (driver.online === true || ['online', 'available', 'busy', 'on_run', 'active'].includes(status)) return 'online';
  if (status === 'offline' || driver.online === false) return 'offline';
  return status || 'unknown';
}


function driverActivitySummary(driver = {}) {
  const active = Number(driver.activeRuns || 0);
  if (active > 0) {
    const eta = Number.isFinite(driver.avgDelivery) ? fmtMins(Math.max(1, Math.round(driver.avgDelivery))) : 'ETA pending';
    return `${active} active run${active > 1 ? 's' : ''} · ${eta}`;
  }
  if (String(driver.status || '').toLowerCase() === 'online') return 'Online · available';
  if (driver.completed) return `${driver.completed} completed today`;
  if (String(driver.status || '').toLowerCase() === 'assigned') return 'At store · collecting order';
  return driver.assignedSite || 'No active run';
}

function orderActivityTime(order = {}, now = Date.now()) {
  const ms = getMs(order.updatedAt || order.raw?.updatedAt || order.createdAt || order.raw?.createdAt);
  if (!ms) return '';
  const mins = Math.max(0, Math.round((now - ms) / 60000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return formatSearchDate(ms);
}

function averageStageMinutes(orders = [], stageNames = [], now = Date.now()) {
  const values = safeArray(orders)
    .filter((order) => stageNames.includes(String(order.stage || '').toLowerCase()))
    .map((order) => attentionStageMinutes(order, now))
    .filter((value) => Number.isFinite(value) && value >= 0);
  if (!values.length) return '—';
  return fmtMins(Math.round(values.reduce((sum, value) => sum + value, 0) / values.length));
}

function driverDeliveryMinutes(order = {}) {
  const t = { ...(order.raw?.timings || {}), ...(order.timings || {}) };
  const start = getMs(t.outForDeliveryAt || t.driverPickedUpAt || t.collectedAt);
  const end = getMs(t.deliveredAt || t.completedAt || order.deliveredAt || order.completedAt || order.archivedAt || order.updatedAt);
  if (!start || !end || end < start) return null;
  return Math.round((end - start) / 60000);
}

function driverReliabilityTone(score) {
  if (score >= 90) return { label: 'Reliable', tone: 'green' };
  if (score >= 70) return { label: 'Watch', tone: 'amber' };
  return { label: 'Intervention', tone: 'red' };
}

function buildDriverIntelligence(orders = [], drivers = [], now = Date.now()) {
  const liveOrders = safeArray(orders).filter((order) => !isArchivedOrReroutedOrder(order));
  const allOrders = safeArray(orders);
  const driverRows = safeArray(drivers);

  const knownDrivers = driverRows.map((driver) => {
    const id = firstText(driver.id, driver.uid, driver.email, driver.name, driverDisplayName(driver));
    const name = cleanDriverName(driver);
    return {
      id,
      name,
      raw: driver,
      isGenericProfile: isGenericDriverName(name) || isGenericDriverName(id),
    };
  });

  return knownDrivers
    .map((driver) => {
      const raw = driver.raw || {};
      const assignedOrders = allOrders.filter((order) => driverMatchesOrder(order, { ...raw, id: driver.id, name: driver.name }));
      const liveAssigned = liveOrders.filter((order) => driverMatchesOrder(order, { ...raw, id: driver.id, name: driver.name }));
      const activeRuns = liveAssigned.filter((order) => ['ready_for_driver', 'out_for_delivery'].includes(String(order.stage || '').toLowerCase()));
      const completedOrders = assignedOrders.filter((order) => isDeliveredLike(order) || ['delivered', 'completed'].includes(String(order.stage || '').toLowerCase()));
      const cancelledOrders = assignedOrders.filter((order) => String(order.stage || order.status || order.raw?.stage || order.raw?.status || '').toLowerCase().includes('cancel'));
      const deliveryDurations = completedOrders.map(driverDeliveryMinutes).filter((mins) => Number.isFinite(mins));
      const activeRouteEtas = activeRuns.map(orderRouteEtaMinutes).filter((mins) => Number.isFinite(mins));
      const avgDelivery = activeRouteEtas.length
        ? Math.round(activeRouteEtas.reduce((sum, mins) => sum + mins, 0) / activeRouteEtas.length)
        : (deliveryDurations.length ? Math.round(deliveryDurations.reduce((sum, mins) => sum + mins, 0) / deliveryDurations.length) : null);
      const onlineStatus = driverOnlineStatus(raw);
      const lateActive = liveAssigned.filter((order) => String(order.stage || '').toLowerCase() === 'out_for_delivery' && attentionStageMinutes(order, now) >= 25);
      const readyWaiting = liveAssigned.filter((order) => String(order.stage || '').toLowerCase() === 'ready_for_driver' && attentionStageMinutes(order, now) >= 10);
      const overallLateActive = liveAssigned.filter((order) => overallSlaStatus(order, now).severity === 'critical');
      const offlineRisk = activeRuns.length > 0 && onlineStatus === 'offline';
      const missedOrCancelled = cancelledOrders.length;
      const acceptanceBase = activeRuns.length + completedOrders.length + missedOrCancelled;
      const acceptanceRate = acceptanceBase ? Math.round(((activeRuns.length + completedOrders.length) / acceptanceBase) * 100) : null;

      let score = 100;
      if (acceptanceRate !== null) score -= Math.max(0, 100 - acceptanceRate) * 0.35;
      score -= Math.max(lateActive.length, overallLateActive.length) * 14;
      score -= readyWaiting.length * 8;
      score -= missedOrCancelled * 6;
      if (offlineRisk) score -= 22;
      if (avgDelivery !== null && avgDelivery > 35) score -= Math.min(20, avgDelivery - 35);
      score = Math.max(0, Math.min(100, Math.round(score)));

      const risks = [];
      if (offlineRisk) risks.push('Offline with active run');
      if (lateActive.length || overallLateActive.length) risks.push(`${Math.max(lateActive.length, overallLateActive.length)} late fulfilment risk${Math.max(lateActive.length, overallLateActive.length) > 1 ? 's' : ''}`);
      if (readyWaiting.length) risks.push(`${readyWaiting.length} ready run waiting`);
      if (avgDelivery !== null && avgDelivery > 35) risks.push(`Avg delivery ${fmtMins(avgDelivery)}`);
      if (missedOrCancelled) risks.push(`${missedOrCancelled} missed / cancelled`);

      const reliability = driverReliabilityTone(score);
      const assignedSite = driverAssignedSite(driver, assignedOrders, liveAssigned);

      return {
        ...driver,
        assignedSite,
        status: onlineStatus,
        activeRuns: activeRuns.length,
        completed: completedOrders.length,
        missedOrCancelled,
        acceptanceRate,
        avgDelivery,
        lateActive: Math.max(lateActive.length, overallLateActive.length),
        readyWaiting: readyWaiting.length,
        score,
        reliability,
        risks,
        pressure: risks.length * 100 + lateActive.length * 40 + readyWaiting.length * 20 + (offlineRisk ? 60 : 0) + (100 - score),
      };
    })
    .filter((driver) => FALLBACK_STORES.includes(driver.assignedSite) || driver.activeRuns || driver.risks.length)
    .filter((driver) => !(driver.isGenericProfile && !driver.activeRuns && !driver.completed && !driver.missedOrCancelled && !driver.risks.length))
    .sort((a, b) => b.pressure - a.pressure || b.score - a.score);
}

function DriverRadarMetric({ label, value }) {
  return (
    <div style={driverMetricBoxStyle}>
      <div style={driverMetricValueStyle}>{value}</div>
      <div style={driverMetricLabelStyle}>{label}</div>
    </div>
  );
}

function findDriverIntel(driverIntel = [], order = {}) {
  const assigned = driverName(order);
  if (!assigned || assigned === 'Not selected') return null;
  return driverIntel.find((driver) => {
    const values = [driver.id, driver.name].flatMap(driverMatchTokens);
    return driverMatchTokens(assigned).some((token) => values.includes(token));
  }) || null;
}

function siteHealthFromCapacity(capacity, critical, delayed) {
  if (critical > 0 || capacity < 45) return { label: 'Risk', tone: 'risk' };
  if (delayed > 0 || capacity < 75) return { label: 'Watch', tone: 'watch' };
  return { label: 'Healthy', tone: 'healthy' };
}

function buildSiteCapacityRows({ stores = [], storeCounts = [], drivers = [] }) {
  return stores.map((store) => {
    const counts = storeCounts.find((row) => row.store === store) || {};
    const siteDrivers = safeArray(drivers).filter((driver) => driver.assignedSite === store);
    const onlineDrivers = siteDrivers.filter((driver) => ['online', 'available', 'assigned'].includes(String(driver.status || '').toLowerCase())).length;
    const activeOrders = Number(counts.active || 0);
    const critical = Number(counts.critical || 0);
    const delayed = Number(counts.delayed || 0);
    const operational = Number(counts.operational || 0);
    const availableDrivers = Math.max(0, onlineDrivers - siteDrivers.reduce((sum, driver) => sum + Number(driver.activeRuns || 0), 0));
    const driverBase = Math.max(1, onlineDrivers);
    let capacity = activeOrders ? Math.round(Math.max(0, Math.min(100, ((driverBase * 2 - activeOrders) / (driverBase * 2)) * 100))) : 100;
    capacity -= critical * 18;
    capacity -= delayed * 10;
    capacity -= operational * 4;
    capacity = Math.max(0, Math.min(100, capacity));
    const health = siteHealthFromCapacity(capacity, critical, delayed);
    return {
      store,
      active: activeOrders,
      critical,
      delayed,
      operational,
      onlineDrivers,
      availableDrivers,
      capacity,
      health,
      pressure: critical * 100 + delayed * 45 + operational * 20 + activeOrders * 12 + Math.max(0, 70 - capacity),
    };
  }).sort((a, b) => b.pressure - a.pressure || a.store.localeCompare(b.store));
}

function searchableText(...values) {
  return values
    .flatMap((value) => Array.isArray(value) ? value : [value])
    .map((value) => String(value || '').toLowerCase())
    .join(' ');
}

function orderSearchText(order = {}) {
  const raw = order.raw || {};
  return searchableText(
    order.id,
    order.orderNumber,
    customerName(order),
    customerPhone(order),
    customerAddress(order),
    orderStore(order),
    sentBy(order),
    packerName(order),
    driverName(order),
    paymentLabel(order),
    stageLabel(order),
    currentOperationalWait(order),
    formatSearchDate(orderSearchDate(order)),
    originalItems(order).map(itemName),
    additionItems(order).map(itemName),
    raw.notes,
    order.notes
  );
}

function archivedOrderSearchText(order = {}) {
  const raw = order.raw || {};
  return searchableText(
    order.id,
    order.orderNumber,
    customerName(order),
    customerPhone(order),
    customerAddress(order),
    orderStore(order),
    paymentLabel(order),
    stageLabel(order),
    archiveReason(order),
    formatSearchDate(orderSearchDate(order)),
    money(liveOrderTotal(order)),
    originalItems(order).map(itemName),
    additionItems(order).map(itemName),
    raw.notes,
    order.notes
  );
}

function driverSearchText(driver = {}) {
  return searchableText(driver.id, driver.name, driver.fullName, driver.displayName, driver.email, driver.phone, driver.status, driver.state, driver.site, driver.store);
}


function normalizeCommandValue(value) {
  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

function orderNumberText(order = {}) {
  return firstText(order.orderNumber, order.raw?.orderNumber, order.id, '000000');
}

function isDeliveredLike(order = {}) {
  const stage = String(order.stage || order.raw?.stage || '').toLowerCase();
  const status = String(order.status || order.raw?.status || '').toLowerCase();
  return ['delivered','completed'].includes(stage) || ['delivered','completed'].includes(status) || Boolean(order.timings?.deliveredAt || order.raw?.timings?.deliveredAt);
}

function archiveReason(order = {}) {
  const raw = order.raw || {};
  return firstText(order.archiveReason, raw.archiveReason, order.cancelReason, raw.cancelReason, order.rerouteReason, raw.rerouteReason, 'Archived order');
}

function orderSearchTags(order = {}, now = Date.now()) {
  const tags = [];
  const balance = orderBalance(order);
  if (Math.abs(balance) > 0.01) tags.push('balance', 'owes', 'payment', 'cod', 'cash');
  if (additionItems(order).length) tags.push('addition', 'swap', 'adjustment', 'replacement');
  if (originalItems(order).some((item, index) => isUnavailable(order, item, index))) tags.push('unavailable', 'out of stock', 'stock issue', 'dispute');
  if (isArchivedOrReroutedOrder(order)) tags.push('archive', 'archived', archiveReason(order));
  if (isDeliveredLike(order)) tags.push('completed', 'delivered');
  timelineRows(order, now).forEach((row) => tags.push(row.title, row.intelligence, row.duration));
  chatMessages(order).forEach((msg) => tags.push(msg.text, msg.message, msg.senderName, msg.author));
  return tags;
}

function makeCommandResults({ query, orders = [], drivers = [], stores = [], attentionItems = [], now = Date.now() }) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return [];
  const normalizedQuery = normalizeCommandValue(q);
  const digitsQuery = q.replace(/\D+/g, '');
  const results = [];
  const liveOrders = orders.filter((order) => !isArchivedOrReroutedOrder(order));
  const archivedOrders = orders.filter((order) => isArchivedOrReroutedOrder(order));

  const addResult = (item) => {
    if (!item) return;
    results.push(item);
  };

  orders.forEach((order) => {
    const number = orderNumberText(order);
    const isArchived = isArchivedOrReroutedOrder(order);
    const searchBody = searchableText(isArchived ? archivedOrderSearchText(order) : orderSearchText(order), isArchived ? [] : orderSearchTags(order, now), number);
    const normalizedBody = normalizeCommandValue(searchBody);
    const numberDigits = String(number).replace(/\D+/g, '');
    const riskMatches = attentionItems.filter((item) => item.order?.id === order.id);
    const keywordRisk = !isArchived && riskMatches.find((item) => searchableText(item.type, item.reason, item.severity, item.action).includes(q));
    const matched = searchBody.includes(q) || normalizedBody.includes(normalizedQuery) || (digitsQuery && numberDigits.includes(digitsQuery)) || keywordRisk;

    if (matched) {
      addResult({
        group: isArchived ? 'Archives' : 'Orders',
        key: `${isArchived ? 'archive' : 'order'}-${order.id}`,
        label: `#${number}`,
        detail: `${customerName(order)} · ${orderStore(order)} · ${formatSearchDate(orderSearchDate(order))} · ${money(liveOrderTotal(order))}`,
        meta: isArchived ? `${firstText(order.archiveCategory, order.status, order.stage, 'Archived')} · ${formatSearchDate(orderSearchDate(order))}` : keywordRisk ? keywordRisk.type : currentOperationalWait(order),
        order,
        archive: isArchived,
        score: isArchived ? 210 : (digitsQuery && numberDigits.includes(digitsQuery) ? 390 : 300),
      });
    }
  });

  attentionItems.forEach((item) => {
    const text = searchableText(item.orderNumber, item.customer, item.site, item.driver, item.type, item.reason, item.severity, item.action);
    if (text.includes(q) || normalizeCommandValue(text).includes(normalizedQuery)) {
      addResult({
        group: 'Risks',
        key: `risk-${item.key}`,
        label: `${item.type} · #${item.orderNumber}`,
        detail: item.reason,
        meta: item.severity,
        order: item.order,
        attentionItem: item,
        score: item.severity === 'critical' ? 430 : item.severity === 'delayed' ? 370 : 330,
      });
    }
  });

  orders.forEach((order) => {
    const timelineMatch = timelineRows(order, now).find((row) => searchableText(row.title, row.intelligence, row.duration, formatClock(row.at)).includes(q));
    if (timelineMatch) {
      addResult({
        group: 'Timeline',
        key: `timeline-${order.id}-${timelineMatch.title}-${getMs(timelineMatch.at)}`,
        label: `${timelineMatch.title} · #${orderNumberText(order)}`,
        detail: `${customerName(order)} · ${formatSearchDate(orderSearchDate(order))} · ${timelineMatch.intelligence || 'Elapsed ' + timelineMatch.duration}`,
        meta: `${formatClock(timelineMatch.at)} · ${formatSearchDate(timelineMatch.at || orderSearchDate(order))}`,
        order,
        archive: isArchivedOrReroutedOrder(order),
        openTab: 'Timeline',
        score: 285,
      });
    }
  });

  drivers.forEach((driver) => {
    if (!driverSearchText(driver).includes(q) && !normalizeCommandValue(driverSearchText(driver)).includes(normalizedQuery)) return;
    const name = firstText(driver.name, driver.fullName, driver.displayName, driver.email, driver.id, 'Driver');
    const driverOrders = liveOrders.filter((order) => searchableText(driverName(order), order.assignedDriver, order.assignedDriverName, order.assignment?.driverId).includes(String(name).toLowerCase()));
    addResult({
      group: 'Drivers',
      key: `driver-${driver.id || name}`,
      label: name,
      detail: `${driverOrders.length} active order${driverOrders.length === 1 ? '' : 's'} · ${driver.status || driver.state || (driver.online ? 'Online' : 'Driver profile')}`,
      meta: firstText(driver.site, driver.store, 'Driver'),
      driver,
      order: driverOrders[0] || null,
      score: 260,
    });
  });

  stores.forEach((store) => {
    const storeOrders = liveOrders.filter((order) => orderStore(order) === store);
    const storeRisks = attentionItems.filter((item) => item.site === store);
    const storeArchiveCount = archivedOrders.filter((order) => orderStore(order) === store).length;
    if (searchableText(store, 'site', 'store', storeOrders.length, storeRisks.length, storeArchiveCount).includes(q)) {
      addResult({
        group: 'Sites',
        key: `site-${store}`,
        label: store,
        detail: `${storeOrders.length} active · ${storeRisks.length} risk signal${storeRisks.length === 1 ? '' : 's'} · ${storeArchiveCount} archived`,
        meta: 'Site',
        site: store,
        score: 240,
      });
    }
  });

  if (['cash', 'cod', 'money', 'payment', 'paid', 'unpaid', 'owed', 'owes', 'balance'].some((term) => q.includes(term))) {
    orders.forEach((order) => {
      const balance = orderBalance(order);
      if (Math.abs(balance) > 0.01 || isCardPayment(order) || /cash|cod/i.test(paymentLabel(order))) {
        addResult({
          group: 'Payments',
          key: `payment-${order.id}`,
          label: `#${orderNumberText(order)} · ${paymentLabel(order)}`,
          detail: `${customerName(order)} · ${formatSearchDate(orderSearchDate(order))} · ${balance > 0 ? 'Customer owes ' + money(balance) : balance < 0 ? 'Refund due ' + money(Math.abs(balance)) : 'No balance'}`,
          meta: isArchivedOrReroutedOrder(order) ? `Archive · ${formatSearchDate(orderSearchDate(order))}` : money(liveOrderTotal(order)),
          order,
          archive: isArchivedOrReroutedOrder(order),
          score: 235,
        });
      }
    });
  }

  if (['swap', 'addition', 'adjustment', 'unavailable', 'stock', 'out of stock', 'replacement'].some((term) => q.includes(term))) {
    orders.forEach((order) => {
      const hasStockIssue = additionItems(order).length || originalItems(order).some((item, index) => isUnavailable(order, item, index));
      if (!hasStockIssue) return;
      addResult({
        group: 'Adjustments',
        key: `adjustment-${order.id}`,
        label: `#${orderNumberText(order)} · Stock movement`,
        detail: `${additionItems(order).length} addition/swap · ${originalItems(order).filter((item, index) => isUnavailable(order, item, index)).length} unavailable`,
        meta: customerName(order),
        order,
        archive: isArchivedOrReroutedOrder(order),
        score: 245,
      });
    });
  }

  const seen = new Set();
  return results
    .filter((item) => {
      if (seen.has(item.key)) return false;
      seen.add(item.key);
      return true;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, 18);
}

function useTicker() {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  return now;
}

export default function OnTimeControlTowerPage() {
  const { orgId, orders, drivers, settings, loading } = useOnTimeLiveData();
  const fulfilmentStores = useMemo(() => {
    const names = getActiveStoreNames(settings || {});
    return names.length ? names : FALLBACK_STORES;
  }, [settings]);
  const { user, claims } = useAuth();
  const now = useTicker();
  const [selectedStore, setSelectedStore] = useState('District Six');
  const [expandedOrderId, setExpandedOrderId] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState('');
  const [activeTab, setActiveTab] = useState('Overview');
  const [chatText, setChatText] = useState('');
  const [busyKey, setBusyKey] = useState('');
  const [error, setError] = useState('');
  const [cancelDraft, setCancelDraft] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [rerouteDraft, setRerouteDraft] = useState(null);
  const [rerouteTargetStore, setRerouteTargetStore] = useState('');
  const [rerouteReason, setRerouteReason] = useState('');
  const [paymentDraft, setPaymentDraft] = useState(null);
  const [paymentMethodDraft, setPaymentMethodDraft] = useState('');
  const [previewEvidence, setPreviewEvidence] = useState(null);
  const [attentionFilter, setAttentionFilter] = useState('all');
  const [commandQuery, setCommandQuery] = useState('');
  const [commandCursor, setCommandCursor] = useState(0);
  const centerScrollRef = useRef(null);
  const approvalFocusRef = useRef('');

  useEffect(() => {
    const syncCommandSearch = (event) => setCommandQuery(event?.detail?.query || '');
    window.addEventListener('link-command-search', syncCommandSearch);
    return () => window.removeEventListener('link-command-search', syncCommandSearch);
  }, []);

  const actor = useMemo(() => ({
    name: user?.displayName || user?.email || 'Control Tower',
    email: user?.email || '',
    role: 'online',
    userId: user?.uid || '',
    orgId: claims?.orgId || orgId || '',
  }), [claims?.orgId, orgId, user]);

  const controlTowerOrders = useMemo(() => (Array.isArray(orders) ? orders : [])
    .filter((order) => fulfilmentStores.includes(orderStore(order)))
    .filter((order) => !isArchivedOrReroutedOrder(order) && !isDemoOrGhostOrder(order)), [orders, fulfilmentStores]);

  const attentionItems = useMemo(() => buildAttentionItems(controlTowerOrders, drivers, now), [controlTowerOrders, drivers, now]);
  const driverIntelligence = useMemo(() => buildDriverIntelligence(orders, drivers, now), [orders, drivers, now]);
  const selectedStoreAllAttention = useMemo(
    () => attentionItems.filter((item) => item.site === selectedStore),
    [attentionItems, selectedStore]
  );
  const selectedAttentionSummary = useMemo(() => attentionCounts(selectedStoreAllAttention), [selectedStoreAllAttention]);
  const globalAttentionSummary = useMemo(() => attentionCounts(attentionItems), [attentionItems]);

  const visibleOrders = useMemo(() => {
    const attentionOrderIds = new Set(
      selectedStoreAllAttention
        .filter((item) => attentionFilter === 'all' || item.severity === attentionFilter)
        .map((item) => item.order.id)
    );

    return controlTowerOrders
      .filter((order) => orderStore(order) === selectedStore)
      .filter((order) => attentionFilter === 'all' || attentionOrderIds.has(order.id))
      .sort((a, b) => {
        const aRisk = selectedStoreAllAttention.filter((item) => item.order.id === a.id).length;
        const bRisk = selectedStoreAllAttention.filter((item) => item.order.id === b.id).length;
        if (aRisk !== bRisk) return bRisk - aRisk;
        const aCritical = approvalRequired(a) || a.issueFlag ? 1 : 0;
        const bCritical = approvalRequired(b) || b.issueFlag ? 1 : 0;
        if (aCritical !== bCritical) return bCritical - aCritical;
        return minutesSince(timerSource(b), now) - minutesSince(timerSource(a), now);
      });
  }, [attentionFilter, controlTowerOrders, now, selectedStore, selectedStoreAllAttention]);

  useEffect(() => {
    if (!selectedOrderId && visibleOrders[0]) setSelectedOrderId(visibleOrders[0].id);
    if (selectedOrderId && visibleOrders.length && !visibleOrders.some((order) => order.id === selectedOrderId)) {
      setSelectedOrderId(visibleOrders[0].id);
      setExpandedOrderId('');
    }
  }, [selectedOrderId, visibleOrders]);

  const selectedOrder = visibleOrders.find((order) => order.id === selectedOrderId) || visibleOrders[0] || null;

  const scrollToOrderApproval = (orderId) => {
    if (!orderId) return;
    const run = () => {
      const target = document.querySelector(`[data-approval-anchor=\"${orderId}\"]`);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      if (centerScrollRef.current) {
        centerScrollRef.current.scrollTo({ top: centerScrollRef.current.scrollHeight, behavior: 'smooth' });
      }
    };
    window.requestAnimationFrame(() => window.setTimeout(run, 60));
  };

  const openOrderFromNotification = (detail = {}) => {
    const targetId = detail.orderId;
    if (!targetId) return;
    const target = orders.find((order) => order.id === targetId);
    if (!target || isArchivedOrReroutedOrder(target)) return;
    setSelectedStore(orderStore(target));
    setSelectedOrderId(target.id);
    setExpandedOrderId(target.id);
    setAttentionFilter('all');

    const focus = String(detail.focus || '').toLowerCase();
    if (focus.includes('chat')) setActiveTab('Chat');
    else if (focus.includes('evidence') || focus.includes('proof')) setActiveTab('Evidence');
    else if (focus.includes('driver')) setActiveTab('Driver');
    else setActiveTab('Timeline');

    window.requestAnimationFrame(() => {
      const targetCard = document.querySelector(`[data-order-card="${target.id}"]`);
      targetCard?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      if (focus.includes('approval') || focus.includes('totals')) {
        approvalFocusRef.current = target.id;
        scrollToOrderApproval(target.id);
      }
    });
  };

  const openOrderForAttention = (item) => {
    if (!item?.order?.id) return;
    setSelectedOrderId(item.order.id);
    setExpandedOrderId(item.order.id);
    const actionText = String(item.action || '').toLowerCase();
    const shouldFocusApproval = actionText.includes('approval');
    const shouldOpenChat = actionText.includes('message') || actionText.includes('chat');

    if (shouldOpenChat) setActiveTab('Chat');
    else if (item.type === 'Missing proof') setActiveTab('Evidence');
    else if (item.type.includes('Driver')) setActiveTab('Driver');
    else setActiveTab('Timeline');

    if (shouldFocusApproval) {
      approvalFocusRef.current = item.order.id;
      scrollToOrderApproval(item.order.id);
    }
  };

  useEffect(() => {
    if (approvalFocusRef.current && expandedOrderId === approvalFocusRef.current) {
      scrollToOrderApproval(approvalFocusRef.current);
      approvalFocusRef.current = '';
    }
  }, [expandedOrderId]);

  useEffect(() => {
    const openFromEvent = (event) => openOrderFromNotification(event?.detail || {});
    window.addEventListener('link-open-order', openFromEvent);

    try {
      const pending = window.sessionStorage.getItem('link-pending-open-order');
      if (pending) {
        window.sessionStorage.removeItem('link-pending-open-order');
        const detail = JSON.parse(pending);
        window.setTimeout(() => openOrderFromNotification(detail), 120);
      }
    } catch (error) {
      console.error('[ControlTower] pending notification open failed', error);
    }

    return () => window.removeEventListener('link-open-order', openFromEvent);
  }, [orders]);

  const storeCounts = useMemo(() => fulfilmentStores.map((store) => {
    const storeOrders = controlTowerOrders.filter((order) => orderStore(order) === store);
    const slaRows = storeOrders.map((order) => overallSlaStatus(order, now));
    const storeAttention = attentionItems.filter((item) => item.site === store);
    return {
      store,
      active: storeOrders.length,
      critical: slaRows.filter((row) => row.severity === 'critical').length,
      delayed: slaRows.filter((row) => row.severity === 'delayed').length,
      operational: Math.max(
        slaRows.filter((row) => row.severity === 'operational').length,
        storeAttention.filter((item) => item.severity === 'operational').length
      ),
    };
  }), [attentionItems, controlTowerOrders, now]);

  const siteCapacityRows = useMemo(
    () => buildSiteCapacityRows({ stores: fulfilmentStores, storeCounts, drivers: driverIntelligence }),
    [driverIntelligence, storeCounts, fulfilmentStores]
  );

  const selectedStoreAttention = useMemo(
    () => selectedStoreAllAttention
      .filter((item) => attentionFilter === 'all' || item.severity === attentionFilter)
      .slice(0, 8),
    [attentionFilter, selectedStoreAllAttention]
  );

  const topAttentionItem = selectedStoreAttention[0] || null;

  const networkSnapshot = useMemo(() => {
    const activeOrders = controlTowerOrders.length;
    const completedToday = safeArray(orders).filter((order) => {
      if (!isDeliveredLike(order)) return false;
      const date = asDate(orderSearchDate(order));
      return date && date.toDateString() === new Date(now).toDateString();
    }).length;
    const slaRows = controlTowerOrders.map((order) => overallSlaStatus(order, now));
    const lateCount = slaRows.filter((row) => row.severity === 'critical').length;
    const riskCount = slaRows.filter((row) => row.severity === 'critical' || row.severity === 'delayed').length;
    const onTimePercent = activeOrders ? Math.max(0, Math.min(100, Math.round(((activeOrders - lateCount) / activeOrders) * 100))) : 100;
    return { activeOrders, completedToday, riskCount, lateCount, onTimePercent };
  }, [controlTowerOrders, now, orders]);

  const recentOrders = useMemo(() => controlTowerOrders
    .slice()
    .sort((a, b) => getMs(b.createdAt || b.raw?.createdAt) - getMs(a.createdAt || a.raw?.createdAt))
    .slice(0, 5), [controlTowerOrders]);

  const topDrivers = useMemo(() => driverIntelligence
    .slice()
    .sort((a, b) => (b.score || 0) - (a.score || 0))
    .slice(0, 5), [driverIntelligence]);

  const commandResults = useMemo(() => makeCommandResults({
    query: commandQuery,
    orders,
    drivers,
    stores: fulfilmentStores,
    attentionItems,
    now,
  }), [attentionItems, commandQuery, drivers, now, orders, fulfilmentStores]);

  useEffect(() => {
    setCommandCursor(0);
  }, [commandQuery]);

  const openCommandResult = (result) => {
    if (!result) return;
    if (result.site) {
      setSelectedStore(result.site);
      setSelectedOrderId('');
      setExpandedOrderId('');
      setAttentionFilter('all');
      window.dispatchEvent(new CustomEvent('link-command-search-clear'));
      setCommandQuery('');
      return;
    }
    if (result.attentionItem) {
      openOrderForAttention(result.attentionItem);
      window.dispatchEvent(new CustomEvent('link-command-search-clear'));
      setCommandQuery('');
      return;
    }
    if (result.order) {
      if (result.archive) {
        window.dispatchEvent(new CustomEvent('link-command-search-clear'));
        setCommandQuery('');
        const orderNumber = encodeURIComponent(orderNumberText(result.order));
        const orderId = encodeURIComponent(result.order.id || '');
        window.location.href = `/ontime/archives?order=${orderNumber}&id=${orderId}`;
        return;
      }
      setSelectedStore(orderStore(result.order));
      setSelectedOrderId(result.order.id);
      setExpandedOrderId(result.order.id);
      setAttentionFilter('all');
      setActiveTab(result.openTab || (result.group === 'Drivers' ? 'Driver' : result.group === 'Payments' ? 'Overview' : 'Timeline'));
      window.requestAnimationFrame(() => {
        const target = document.querySelector(`[data-order-card=\"${result.order.id}\"]`);
        target?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
      window.dispatchEvent(new CustomEvent('link-command-search-clear'));
      setCommandQuery('');
    }
  };

  const runAction = async (key, task) => {
    setBusyKey(key);
    setError('');
    try {
      await task();
    } catch (err) {
      console.error('[ControlTower] action failed', err);
      setError(err?.message || 'Action failed.');
    } finally {
      setBusyKey('');
    }
  };

  const approveAdjustment = (order) => runAction(`approve-${order.id}`, async () => {
    await controlTowerApproveAdjustment({ orgId, orderId: order.id, actor });
  });

  const openPaymentMethodModal = (order) => {
    setPaymentDraft(order);
    setPaymentMethodDraft(paymentLabel(order));
  };

  const requestRerouteOrder = (order, targetStore) => {
    setRerouteDraft(order);
    setRerouteTargetStore(targetStore || fulfilmentStores.find((store) => store !== orderStore(order)) || '');
    setRerouteReason('');
  };

  const confirmRerouteOrder = () => {
    if (!rerouteDraft || !rerouteTargetStore || !rerouteReason.trim()) return;
    const order = rerouteDraft;
    runAction(`reroute-${order.id}`, async () => {
      await controlTowerSendToDifferentStore({
        orgId,
        orderId: order.id,
        order,
        targetStore: rerouteTargetStore,
        reason: rerouteReason.trim(),
        actor,
      });
      setExpandedOrderId('');
      setSelectedOrderId('');
      setRerouteDraft(null);
      setRerouteTargetStore('');
      setRerouteReason('');
    });
  };

  const requestCancelOrder = (order) => {
    setCancelDraft(order);
    setCancelReason('');
  };

  const confirmCancelOrder = () => {
    if (!cancelDraft || !cancelReason.trim()) return;
    const order = cancelDraft;
    runAction(`cancel-${order.id}`, async () => {
      await controlTowerCancelOrder({
        orgId,
        orderId: order.id,
        order,
        reason: cancelReason.trim(),
        actor,
      });
      setExpandedOrderId('');
      setSelectedOrderId('');
      setCancelDraft(null);
      setCancelReason('');
    });
  };

  const sendChat = () => {
    if (!selectedOrder || !chatText.trim()) return;
    const text = chatText.trim();
    setChatText('');
    runAction(`chat-${selectedOrder.id}`, async () => {
      await linkOrderSendMessage({ orgId, orderId: selectedOrder.id, actor, text });
    });
  };

  const toggleOrder = (order) => {
    setSelectedOrderId(order.id);
    setExpandedOrderId((current) => (current === order.id ? '' : order.id));
  };

  const renderRightPanel = () => {
    if (!selectedOrder) return <EmptyPanel text="Select an order to investigate." />;
    const chats = chatMessages(selectedOrder);
    const timeline = timelineRows(selectedOrder, now);
    const evidence = evidenceCards(selectedOrder);
    const durations = orderDurations(selectedOrder, now);
    const currentStage = timelineStageIntelligence(selectedOrder, now);
    const stageFlow = timelineFlowRows(selectedOrder, now);

    if (activeTab === 'Timeline') {
      return <PanelCard title="Operational timeline">
        <TimelineStageCard stage={currentStage} />
        <div style={timelineFlowStyle}>
          {stageFlow.map((step) => <TimelineStep key={step.key} step={step} />)}
        </div>
        <div style={{ marginTop: 18 }}>
          {timeline.map((item, index) => <TimelineRow key={`${item.title}-${index}`} item={item} />)}
        </div>
      </PanelCard>;
    }

    if (activeTab === 'Chat') {
      return <PanelCard title="Live order chat">
        <div style={{ maxHeight: 360, overflow: 'auto', paddingRight: 4 }}>
          {chats.length ? chats.map((chat, index) => <ChatBubble key={chat.id || index} chat={chat} />) : <div style={{ opacity: 0.65 }}>No messages yet.</div>}
        </div>
        <div style={chatComposeStyle}>
          <input
            value={chatText}
            onChange={(event) => setChatText(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') sendChat(); }}
            placeholder="Reply to store / driver chat..."
            style={inputStyle}
          />
          <button type="button" onClick={sendChat} disabled={!chatText.trim() || busyKey.startsWith('chat-')} style={smallActionButton}>Send</button>
        </div>
      </PanelCard>;
    }

    if (activeTab === 'Evidence') {
      return <div>
        <SectionTitle>Operational evidence</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {evidence.map((item) => <EvidenceCard key={item.title} item={item} onPreview={setPreviewEvidence} />)}
        </div>
      </div>;
    }

    if (activeTab === 'Driver') {
      const assigned = driverName(selectedOrder);
      const intel = findDriverIntel(driverIntelligence, selectedOrder);
      const scoreTone = intel?.reliability?.tone === 'green' ? driverScoreGoodStyle : intel?.reliability?.tone === 'amber' ? driverScoreWatchStyle : driverScoreRiskStyle;
      return <div>
        <PanelCard title="Driver intelligence">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: 24, fontWeight: 900 }}>{assigned}</div>
              <div style={{ marginTop: 8, opacity: 0.72 }}>{assigned === 'Not selected' ? 'Waiting for store driver selection' : `${intel?.status || 'assigned'} · ${intel?.reliability?.label || 'No score'}`}</div>
            </div>
            <span style={{ ...driverScorePillStyle, ...scoreTone }}>{intel ? `${intel.score}%` : 'No score'}</span>
          </div>
          <div style={driverMiniGridStyle}>
            <Metric title="Active runs" value={intel ? intel.activeRuns : '—'} />
            <Metric title="Completed" value={intel ? intel.completed : '—'} />
            <Metric title="Accept rate" value={intel?.acceptanceRate !== null && intel?.acceptanceRate !== undefined ? `${intel.acceptanceRate}%` : '—'} />
            <Metric title="Avg delivery" value={intel?.avgDelivery !== null && intel?.avgDelivery !== undefined ? fmtMins(intel.avgDelivery) : '—'} />
          </div>
          <InfoLine label="Late active runs" value={intel?.lateActive ? `${intel.lateActive}` : '—'} />
          <InfoLine label="Missed / cancelled" value={intel?.missedOrCancelled ? `${intel.missedOrCancelled}` : '—'} />
          <InfoLine label="GPS" value={intel?.status === 'online' ? 'Live feed active' : 'Not live yet'} />
          {intel?.risks?.length ? <div style={driverRiskListStyle}>{intel.risks.map((risk) => <span key={risk} style={driverRiskChipStyle}>{risk}</span>)}</div> : <div style={driverCleanStyle}>No active driver risk</div>}
        </PanelCard>
        <div style={mapPlaceholderStyle}>{intel?.status === 'online' ? 'LIVE DRIVER MAP' : 'Driver map appears when driver is online'}</div>
      </div>;
    }

    const lastTwoChats = chats.slice(-2);
    return <div>
      <SectionTitle>Operational overview</SectionTitle>
      <TimelineStageCard stage={currentStage} compact />
      <div style={metricGridStyle}>
        <Metric title="Waiting" value={durations.waiting} />
        <Metric title="Packing" value={durations.packing} />
        <Metric title="Ready" value={durations.ready} />
        <Metric title="Overall" value={durations.overall} />
      </div>
      <PanelCard title="Last 2 chat messages" style={{ marginTop: 16 }}>
        {lastTwoChats.length ? lastTwoChats.map((chat, index) => <ChatBubble key={chat.id || index} chat={chat} compact />) : <div style={{ opacity: 0.65 }}>No chat messages yet.</div>}
      </PanelCard>
    </div>;
  };

  return (
    <div style={pageStyle}>
      {commandQuery.trim() ? <CommandSearchPanel query={commandQuery} results={commandResults} onOpen={openCommandResult} activeIndex={commandCursor} /> : null}
      <aside style={leftStyle}>
        <div style={{ fontSize: 42, fontWeight: 800 }}>Link</div>
        <div style={attentionStyle}>
          <div style={eyebrow}>Attention Mode</div>
          <div style={{ marginTop: 10, fontSize: 40, fontWeight: 800 }}>{globalAttentionSummary.critical + globalAttentionSummary.delayed}</div>
          <div style={{ marginTop: 8, opacity: 0.7 }}>Live risk signals</div>
        </div>
        <div style={topStoreRailStyle}>
          <div style={topStoreRailLabelStyle}>Sites</div>
          {siteCapacityRows.map((row) => {
            const toneStyle = row.health.tone === 'risk' ? siteRiskCardStyle : row.health.tone === 'watch' ? siteWatchCardStyle : siteHealthyCardStyle;
            return (
              <button
                key={row.store}
                type="button"
                onClick={() => { setSelectedStore(row.store); setExpandedOrderId(''); setSelectedOrderId(''); }}
                style={{ ...storeButtonStyle, ...toneStyle, ...(selectedStore === row.store ? selectedStoreButtonStyle : {}) }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <strong>{row.store}</strong>
                  <span style={{ ...siteHealthPillStyle, ...(row.health.tone === 'risk' ? siteRiskPillStyle : row.health.tone === 'watch' ? siteWatchPillStyle : siteHealthyPillStyle) }}>{row.health.label}</span>
                </div>
                <div style={siteCardGridStyle}>
                  <div><strong>{row.active}</strong><span>active</span></div>
                  <div><strong>{row.onlineDrivers}</strong><span>drivers</span></div>
                  <div><strong>{row.critical}</strong><span>critical</span></div>
                  <div><strong>{row.capacity}%</strong><span>capacity</span></div>
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {row.delayed ? <small style={miniDelayedPillStyle}>{row.delayed} delayed</small> : null}
                  {row.operational ? <small style={miniOperationalPillStyle}>{row.operational} operational</small> : null}
                  {!row.critical && !row.delayed && !row.operational ? <small style={miniHealthyPillStyle}>clean</small> : null}
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <section style={commandCenterStyle}>
        <div style={networkKpiGridStyle}>
          <NetworkKpiCard icon="▣" title="Live orders" value={networkSnapshot.activeOrders} delta="active across fulfilment" tone="blue" />
          <NetworkKpiCard icon="✓" title="On time" value={`${networkSnapshot.onTimePercent}%`} delta="based on live risk" tone="green" />
          <NetworkKpiCard icon="⚠" title="At risk" value={networkSnapshot.riskCount} delta="attention signals" tone="amber" />
          <NetworkKpiCard icon="⏱" title="Late" value={networkSnapshot.lateCount} delta="critical now" tone="red" />
          <NetworkKpiCard icon="⇢" title="Delivered today" value={networkSnapshot.completedToday} delta="completed flow" tone="purple" />
        </div>

        <div style={operationsGridStyle}>
          <LiveOperationsMap
            stores={siteCapacityRows}
            orders={controlTowerOrders}
            drivers={driverIntelligence}
            selectedStore={selectedStore}
            onSelectStore={(store) => { setSelectedStore(store); setExpandedOrderId(''); setSelectedOrderId(''); }}
          />

          <OrderFlowPanel orders={controlTowerOrders} now={now} />

          <div style={commandRightRailStyle}>
            <MiniCommandPanel title="Critical Alerts" action="View all">
              {(attentionItems.length ? attentionItems.slice(0, 4) : []).map((item) => (
                <button key={item.key} type="button" onClick={() => openOrderForAttention(item)} style={{ ...miniAlertStyle, ...(item.severity === 'critical' ? miniAlertCriticalStyle : item.severity === 'delayed' ? miniAlertDelayedStyle : miniAlertOperationalStyle) }}>
                  <span style={{ ...alertDotIconStyle, ...(item.severity === 'critical' ? alertDotCriticalStyle : item.severity === 'delayed' ? alertDotDelayedStyle : alertDotOperationalStyle) }}>⏱</span>
                  <div style={{ minWidth: 0 }}>
                    <div style={miniAlertTitleRowStyle}>
                      <strong>{item.type} · {item.site}</strong>
                      <span style={{ ...miniAlertTagStyle, ...(item.severity === 'critical' ? miniAlertTagCriticalStyle : item.severity === 'delayed' ? miniAlertTagDelayedStyle : miniAlertTagOperationalStyle) }}>{item.severity === 'critical' ? 'High' : item.severity === 'delayed' ? 'Medium' : 'Info'}</span>
                    </div>
                    <div style={miniAlertBodyStyle}>#{item.orderNumber} has waited {item.timer}</div>
                  </div>
                  <time style={miniAlertTimeStyle}>{formatClock(orderSearchDate(item.order || {}))}</time>
                </button>
              ))}
              {!attentionItems.length ? <div style={miniEmptyStyle}>No critical alerts. Clean flow.</div> : null}
            </MiniCommandPanel>

            <MiniCommandPanel title="Driver Activity" action="View all">
              {topDrivers.map((driver) => (
                <div key={driver.id || driver.name} style={driverActivityRowStyle}>
                  <span style={driverAvatarMiniStyle}>{String(driver.name || 'D').slice(0, 1).toUpperCase()}</span>
                  <div style={{ minWidth: 0 }}>
                    <strong>{driver.name}</strong>
                    <div style={driverActivityMetaStyle}>
                      <span style={{ ...driverStatusDotStyle, background: String(driver.status || '').toLowerCase() === 'online' ? '#34d399' : String(driver.status || '').toLowerCase() === 'assigned' ? '#f59e0b' : '#64748b' }} />
                      {driver.status === 'online' ? 'Online' : driver.status === 'assigned' ? 'At store' : 'Offline'}
                    </div>
                  </div>
                  <div style={driverActivityTaskStyle}>{driverActivitySummary(driver)}</div>
                </div>
              ))}
              {!topDrivers.length ? <div style={miniEmptyStyle}>No drivers monitored yet.</div> : null}
            </MiniCommandPanel>

            <MiniCommandPanel title="Recent Orders" action="View all">
              {recentOrders.map((order) => (
                <button key={order.id} type="button" onClick={() => { setSelectedStore(orderStore(order)); setSelectedOrderId(order.id); setExpandedOrderId(order.id); }} style={recentOrderRowStyle}>
                  <div style={recentOrderMainStyle}>
                    <strong style={recentOrderNumberStyle}>#{orderNumberText(order)}</strong>
                    <span style={recentOrderStoreStyle}>{orderStore(order)}</span>
                  </div>
                  <div style={recentOrderSideStyle}>
                    <em style={recentOrderTimeStyle}>{orderActivityTime(order, now)}</em>
                    <small style={recentOrderStatusPillStyle}>{stageLabel(order)}</small>
                  </div>
                </button>
              ))}
              {!recentOrders.length ? <div style={miniEmptyStyle}>No live orders.</div> : null}
            </MiniCommandPanel>
          </div>
        </div>

        <div style={performanceStripStyle}>
          <SlaPerformanceCard snapshot={networkSnapshot} />
          <SitePerformanceCard rows={siteCapacityRows} onSelectStore={(store) => setSelectedStore(store)} />
          <TopDriversCard drivers={topDrivers} />
        </div>

        <OverviewBottomMetrics siteRows={siteCapacityRows} drivers={driverIntelligence} snapshot={networkSnapshot} />
      </section>

      <main ref={centerScrollRef} style={centerStyle}>
        <section style={headerCardStyle}>
          <div style={eyebrow}>Site Capacity Intelligence</div>
          <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 38, fontWeight: 800 }}>{selectedStore}</div>
              <div style={{ marginTop: 8, opacity: 0.72 }}>Capacity, driver cover, active orders and risk pressure for the selected site.</div>
            </div>
            {siteCapacityRows.filter((row) => row.store === selectedStore).map((row) => (
              <div key={row.store} style={siteCapacitySummaryStyle}>
                <span style={{ ...siteHealthPillStyle, ...(row.health.tone === 'risk' ? siteRiskPillStyle : row.health.tone === 'watch' ? siteWatchPillStyle : siteHealthyPillStyle) }}>{row.health.label}</span>
                <strong>{row.capacity}%</strong>
                <small>capacity</small>
              </div>
            ))}
          </div>
        </section>


        <section style={attentionEngineStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={eyebrow}>Attention Engine</div>
              <div style={{ marginTop: 8, fontSize: 32, fontWeight: 900 }}>Live operational risk</div>
              <div style={{ marginTop: 6, opacity: 0.72 }}>Auto-detecting delays, proof gaps, driver risk and order adjustments.</div>
            </div>

            <div style={attentionSummaryStyle}>
              <AttentionSummaryPill label="All" value={selectedStoreAllAttention.length} tone="all" active={attentionFilter === 'all'} onClick={() => setAttentionFilter('all')} />
              <AttentionSummaryPill label="Critical" value={selectedAttentionSummary.critical} tone="critical" active={attentionFilter === 'critical'} onClick={() => setAttentionFilter('critical')} />
              <AttentionSummaryPill label="Delayed" value={selectedAttentionSummary.delayed} tone="delayed" active={attentionFilter === 'delayed'} onClick={() => setAttentionFilter('delayed')} />
              <AttentionSummaryPill label="Operational" value={selectedAttentionSummary.operational} tone="operational" active={attentionFilter === 'operational'} onClick={() => setAttentionFilter('operational')} />
            </div>
          </div>

          {topAttentionItem ? <div style={nextMoveStyle}>
            <div>
              <div style={eyebrow}>Next best move</div>
              <div style={{ marginTop: 6, fontSize: 18, fontWeight: 900 }}>#{topAttentionItem.orderNumber} · {topAttentionItem.type}</div>
              <div style={{ marginTop: 4, opacity: 0.72 }}>{topAttentionItem.reason}</div>
            </div>
            <button
              type="button"
              style={nextMoveButtonStyle}
              onClick={() => openOrderForAttention(topAttentionItem)}
            >{topAttentionItem.action}</button>
          </div> : null}

          {selectedStoreAttention.length ? (
            <div style={attentionFeedStyle}>
              {selectedStoreAttention.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => openOrderForAttention(item)}
                  style={{ ...attentionCardStyle, ...(item.severity === 'critical' ? attentionCriticalStyle : item.severity === 'delayed' ? attentionDelayedStyle : attentionOperationalStyle) }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                    <strong>#{item.orderNumber}</strong>
                    <span style={{ ...attentionSeverityPillStyle, ...(item.severity === 'critical' ? attentionCriticalPillStyle : item.severity === 'delayed' ? attentionDelayedPillStyle : attentionOperationalPillStyle) }}>
                      {item.severity}
                    </span>
                  </div>
                  <div style={{ marginTop: 8, fontWeight: 800 }}>{item.type}</div>
                  <div style={{ marginTop: 6, opacity: 0.72, lineHeight: 1.45 }}>{item.reason}</div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 12, opacity: 0.78 }}>
                    <span>{item.customer}</span>
                    <strong>{item.timer}</strong>
                  </div>
                  <div style={{ marginTop: 10, color: '#8fe9ff', fontWeight: 800 }}>{item.action}</div>
                </button>
              ))}
            </div>
          ) : (
            <div style={attentionEmptyStyle}>No active attention items for {selectedStore}.</div>
          )}
        </section>


        <section style={driverIntelligenceStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            <div>
              <div style={eyebrow}>Driver Intelligence</div>
              <div style={{ marginTop: 8, fontSize: 28, fontWeight: 900 }}>Driver pressure radar</div>
              <div style={{ marginTop: 6, opacity: 0.72 }}>Reliability score, pressure ranking, active risk and availability by site.</div>
            </div>
            <span style={driverMonitorPillStyle}>{driverIntelligence.length} monitored</span>
          </div>

          <div style={driverRadarGridStyle}>
            {driverIntelligence.map((driver) => {
              const scoreTone = driver.reliability.tone === 'green' ? driverScoreGoodStyle : driver.reliability.tone === 'amber' ? driverScoreWatchStyle : driverScoreRiskStyle;
              return (
                <div key={driver.id || driver.name} style={driverRadarCardStyle}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontWeight: 900 }}>{driver.name}</div>
                      <div style={{ marginTop: 6, opacity: 0.68 }}>{driver.assignedSite} · {driver.reliability.label}</div>
                      <div style={{ marginTop: 4, opacity: 0.55, fontSize: 12 }}>{driver.status}</div>
                    </div>
                    <span style={{ ...driverScorePillStyle, ...scoreTone }}>{driver.score}%</span>
                  </div>
                  <div style={driverStatsRowStyle}>
                    <DriverRadarMetric label="Active" value={driver.activeRuns} />
                    <DriverRadarMetric label="Completed" value={driver.completed} />
                    <DriverRadarMetric label="Accept" value={driver.acceptanceRate !== null && driver.acceptanceRate !== undefined ? `${driver.acceptanceRate}%` : '—'} />
                    <DriverRadarMetric label="Avg delivery" value={driver.avgDelivery !== null && driver.avgDelivery !== undefined ? fmtMins(driver.avgDelivery) : '—'} />
                  </div>
                  {driver.risks.length ? <div style={driverRiskListStyle}>{driver.risks.slice(0, 3).map((risk) => <span key={risk} style={driverRiskChipStyle}>{risk}</span>)}</div> : <div style={driverCleanStyle}>No active driver risk</div>}
                </div>
              );
            })}
            {!driverIntelligence.length ? <div style={attentionEmptyStyle}>No driver profiles or assigned drivers found yet.</div> : null}
          </div>
        </section>

        <section style={ordersPanelStyle}>
          {loading && !orders.length ? <EmptyPanel text="Loading live Link orders..." /> : null}
          {!loading && !visibleOrders.length ? <EmptyPanel text={attentionFilter === 'all' ? `No active orders for ${selectedStore}.` : `No ${attentionFilter} orders for ${selectedStore}.`} /> : null}
          {visibleOrders.map((order) => {
            const expanded = expandedOrderId === order.id;
            const paidTotal = originalPaidTotal(order);
            const liveTotal = liveOrderTotal(order);
            const balance = liveTotal - paidTotal;
            const needsApproval = approvalRequired(order);
            const approved = Boolean(order.swapApproved);
            const durations = orderDurations(order, now);
            const targetStore = fulfilmentStores.find((store) => store !== orderStore(order)) || 'Parow';
            return <article key={order.id} data-order-card={order.id} style={{ ...orderCardStyle, ...(expanded ? expandedOrderCardStyle : {}) }}>
              <button type="button" onClick={() => toggleOrder(order)} style={orderHeaderButtonStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 18 }}>
                  <div>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                      <h2 style={{ margin: 0, fontSize: 30 }}>#{order.orderNumber || order.id}</h2>
                      {approved ? <StatusPill label="Approved" tone="green" /> : needsApproval ? <StatusPill label="Approval needed" tone="amber" /> : <StatusPill label={stageLabel(order)} tone="blue" />}
                    </div>
                    <p style={{ margin: '8px 0 0', opacity: 0.72 }}>{customerName(order)} · Sent by <strong>{sentBy(order)}</strong></p>
                    <OrderRiskStrip items={attentionItems.filter((item) => item.order.id === order.id).slice(0, 3)} />
                  </div>
                  <div style={delayBadgeStyle}>{orderDurations(order, now).overall}</div>
                </div>
                <div style={orderInfoGridStyle}>
                  <OrderInfo label="Store" value={orderStore(order)} />
                  <OrderInfo label="Packer" value={packerName(order)} />
                  <OrderInfo label="Driver" value={driverName(order)} />
                  <OrderInfo label="Waiting for" value={currentOperationalWait(order)} />
                  <OrderInfo label="Timer" value={durations.overall} />
                </div>
              </button>

              {expanded ? <div style={expandedBodyStyle}>
                <div style={detailGridStyle}>
                  <PanelCard title="Customer">
                    <div style={{ fontSize: 24, fontWeight: 800 }}>{customerName(order)}</div>
                    <div style={{ marginTop: 10, opacity: 0.72 }}>{customerPhone(order)}</div>
                    <div style={{ marginTop: 8, opacity: 0.72 }}>{customerAddress(order)}</div>
                    {order.notes ? <div style={{ marginTop: 12, opacity: 0.72 }}>{order.notes}</div> : null}
                  </PanelCard>
                  <div data-approval-anchor={order.id}>
                    <PanelCard title="Approval & reroute">
                      <div style={{ opacity: 0.72, lineHeight: 1.6 }}>
                        {needsApproval ? 'Review the totals difference before approving the store replacement.' : approved ? 'Replacement approved. Store can start packing.' : 'No approval required right now.'}
                      </div>
                      <div style={{ display: 'grid', gap: 12, marginTop: 18 }}>
                        {needsApproval ? <button type="button" style={primaryActionButton} disabled={busyKey === `approve-${order.id}`} onClick={() => approveAdjustment(order)}>Approve adjustment</button> : null}
                        <button type="button" style={smallActionButton} onClick={() => openPaymentMethodModal(order)}>Change payment method</button>
                        <button type="button" style={smallActionButton} disabled={busyKey === `reroute-${order.id}`} onClick={() => requestRerouteOrder(order, targetStore)}>Send to different store</button>
                        <button type="button" style={dangerActionButton} disabled={busyKey === `cancel-${order.id}`} onClick={() => requestCancelOrder(order)}>Cancel order</button>
                      </div>
                    </PanelCard>
                  </div>
                </div>

                <div style={detailGridStyle}>
                  <PanelCard title="Pack list">
                    {originalItems(order).length ? originalItems(order).map((item, index) => {
                      const unavailable = isUnavailable(order, item, index);
                      return <div key={`${itemKey(item, index)}-${index}`} style={{ ...lineItemStyle, ...(unavailable ? unavailableLineStyle : {}) }}>
                        <span><strong>{itemQty(item)} ×</strong> {itemName(item)} {unavailable ? <small style={unavailablePillStyle}>Unavailable</small> : null}</span>
                        <strong>{money(itemPrice(item))}</strong>
                      </div>;
                    }) : <div style={{ opacity: 0.65 }}>No items attached.</div>}
                    {additionItems(order).length ? <div style={{ marginTop: 18 }}>
                      <SectionTitle>Additions</SectionTitle>
                      {additionItems(order).map((item, index) => <div key={`${itemKey(item, index)}-add-${index}`} style={additionLineStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                          <span><strong>{itemQty(item)} ×</strong> {itemName(item)}</span>
                          <strong>{money(itemPrice(item))}</strong>
                        </div>
                        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
                          <span style={{ opacity: 0.72 }}>{approved ? 'Approved' : 'Pending approval'}</span>
                          {!approved ? <button type="button" style={smallActionButton} disabled={busyKey === `approve-${order.id}`} onClick={() => approveAdjustment(order)}>Approve</button> : null}
                        </div>
                      </div>)}
                    </div> : null}
                  </PanelCard>

<TotalsCard order={order} paidTotal={paidTotal} liveTotal={liveTotal} balance={balance} />
                </div>
              </div> : null}
            </article>;
          })}
        </section>
      </main>

      <aside style={rightStyle}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {TABS.map((tab) => <button key={tab} type="button" onClick={() => setActiveTab(tab)} style={{ ...tabButtonStyle, ...(activeTab === tab ? activeTabButtonStyle : {}) }}>{tab}</button>)}
        </div>
        {selectedOrder ? <div style={{ marginTop: 24 }}>
          <div style={{ fontSize: 38, fontWeight: 800 }}>#{selectedOrder.orderNumber || selectedOrder.id}</div>
          <div style={{ marginTop: 8, opacity: 0.72 }}>{customerName(selectedOrder)}</div>
        </div> : null}
        {error ? <div style={errorStyle}>{error}</div> : null}
        <div style={{ marginTop: 24 }}>{renderRightPanel()}</div>
      </aside>

      {rerouteDraft ? <div style={modalBackdropStyle}>
        <div style={modalCardStyle}>
          <SectionTitle>Send to different store</SectionTitle>
          <h2 style={{ margin: '0 0 10px', fontSize: 28 }}>Confirm reroute</h2>
          <p style={{ margin: '0 0 16px', opacity: 0.72 }}>
            The current order will be archived for audit and a clean order will be created for the selected store.
          </p>

          <label style={{ display: 'block', marginBottom: 10, fontWeight: 800 }}>New store</label>
          <select
            value={rerouteTargetStore}
            onChange={(event) => setRerouteTargetStore(event.target.value)}
            style={{ ...inputStyle, width: '100%', height: 52 }}
          >
            {fulfilmentStores.filter((store) => store !== orderStore(rerouteDraft)).map((store) => (
              <option key={store} value={store}>{store}</option>
            ))}
          </select>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 16 }}>
            <OverviewMetric title="Distance" value={storeDistanceEstimate(rerouteTargetStore).distance} />
            <OverviewMetric title="ETA" value={storeDistanceEstimate(rerouteTargetStore).eta} />
            <OverviewMetric title="Current time" value={orderDurations(rerouteDraft, now).overall} />
          </div>

          <label style={{ display: 'block', marginTop: 16, marginBottom: 10, fontWeight: 800 }}>Reason</label>
          <textarea
            value={rerouteReason}
            onChange={(event) => setRerouteReason(event.target.value)}
            placeholder="Example: stock unavailable at current store, customer approved reroute, or operational delay."
            style={textareaStyle}
          />

          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" style={smallActionButton} onClick={() => { setRerouteDraft(null); setRerouteTargetStore(''); setRerouteReason(''); }}>Back</button>
            <button type="button" style={primaryActionButton} disabled={!rerouteTargetStore || !rerouteReason.trim() || busyKey.startsWith('reroute-')} onClick={confirmRerouteOrder}>
              Confirm reroute
            </button>
          </div>
        </div>
      </div> : null}

      {paymentDraft ? <div style={modalBackdropStyle}>
        <div style={modalCardStyle}>
          <SectionTitle>Change payment method</SectionTitle>
          <h2 style={{ margin: '0 0 10px', fontSize: 28 }}>Select payment option</h2>
          <p style={{ margin: '0 0 16px', opacity: 0.72 }}>
            Choose from the active Link payment methods configured in Settings.
          </p>
          <select
            value={paymentMethodDraft}
            onChange={(event) => setPaymentMethodDraft(event.target.value)}
            style={{ ...inputStyle, width: '100%', height: 52 }}
          >
            {['Cash on delivery', 'Card payment', 'EFT', 'Paid online'].map((method) => (
              <option key={method} value={method}>{method}</option>
            ))}
          </select>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" style={smallActionButton} onClick={() => { setPaymentDraft(null); setPaymentMethodDraft(''); }}>Cancel</button>
            <button type="button" style={primaryActionButton} onClick={() => { setPaymentDraft(null); setPaymentMethodDraft(''); }}>
              Save method
            </button>
          </div>
        </div>
      </div> : null}

      {cancelDraft ? <div style={modalBackdropStyle}>
        <div style={modalCardStyle}>
          <SectionTitle>Cancel order</SectionTitle>
          <h2 style={{ margin: '0 0 10px', fontSize: 28 }}>Reason required</h2>
          <p style={{ margin: '0 0 16px', opacity: 0.72 }}>
            This order will be archived for admin audit and removed from the live Control Tower.
          </p>
          <textarea
            value={cancelReason}
            onChange={(event) => setCancelReason(event.target.value)}
            placeholder="Example: Customer no longer wants the order"
            style={textareaStyle}
          />
          <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
            <button type="button" style={smallActionButton} onClick={() => { setCancelDraft(null); setCancelReason(''); }}>Back</button>
            <button type="button" style={dangerActionButton} disabled={!cancelReason.trim() || busyKey.startsWith('cancel-')} onClick={confirmCancelOrder}>
              Confirm cancel
            </button>
          </div>
        </div>
      </div> : null}

      {previewEvidence ? <div style={modalBackdropStyle}>
        <div style={{ ...modalCardStyle, maxWidth: 760 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div>
              <SectionTitle>{previewEvidence.title}</SectionTitle>
              <div style={{ opacity: 0.72 }}>{formatClock(previewEvidence.time)}</div>
            </div>
            <button type="button" style={smallActionButton} onClick={() => setPreviewEvidence(null)}>Close</button>
          </div>
          <div style={{ marginTop: 18, borderRadius: 18, overflow: 'hidden', background: '#081423' }}>
            <img src={previewEvidence.url} alt={previewEvidence.title} style={{ width: '100%', maxHeight: '70vh', objectFit: 'contain', display: 'block' }} />
          </div>
        </div>
      </div> : null}
    </div>
  );
}

function NetworkKpiCard({ icon, title, value, delta, tone }) {
  const toneStyle = kpiToneStyles[tone] || kpiToneStyles.blue;
  return <div style={{ ...networkKpiCardStyle, ...toneStyle.card }}>
    <div style={{ ...networkKpiIconStyle, ...toneStyle.icon }}>{icon}</div>
    <div>
      <div style={networkKpiTitleStyle}>{title}</div>
      <div style={networkKpiValueStyle}>{value}</div>
      <div style={networkKpiDeltaStyle}>{delta}</div>
    </div>
  </div>;
}

function stableHashPercent(value = '', salt = 0, min = 18, max = 82) {
  const str = String(value || 'unknown');
  let hash = 0;
  for (let index = 0; index < str.length; index += 1) hash = ((hash << 5) - hash) + str.charCodeAt(index) + salt;
  const span = Math.max(1, max - min);
  return min + (Math.abs(hash) % span);
}

function mapPointForStore(store, index = 0) {
  const markerLayout = {
    'District Six': { x: 38, y: 58 },
    Parow: { x: 58, y: 37 },
    Bothasig: { x: 52, y: 28 },
    'Kuils River': { x: 74, y: 45 },
    Crawford: { x: 44, y: 64 },
    Woodstock: { x: 35, y: 44 },
    Foreshore: { x: 31, y: 38 },
    'Sea Point': { x: 22, y: 42 },
    'Camps Bay': { x: 18, y: 58 },
    Milnerton: { x: 47, y: 26 },
  };
  return markerLayout[store] || { x: 22 + ((index * 17) % 62), y: 28 + ((index * 13) % 48) };
}

function mapPointForOrder(order = {}, index = 0) {
  const addressSeed = `${customerAddress(order)}-${customerName(order)}-${orderNumberText(order)}-${index}`;
  return {
    x: stableHashPercent(addressSeed, 11, 14, 86),
    y: stableHashPercent(addressSeed, 29, 22, 82),
  };
}

function mapRouteColor(index = 0) {
  const colors = ['#22d3ee', '#8b5cf6', '#fb923c', '#34d399', '#60a5fa', '#f472b6', '#facc15'];
  return colors[index % colors.length];
}

function mapStageTone(order = {}) {
  const stage = String(order.stage || '').toLowerCase();
  const status = String(order.status || '').toLowerCase();
  if (isDeliveredLike(order)) return { color: '#34d399', label: 'Delivered' };
  if (stage.includes('out') || stage.includes('delivery') || status.includes('route')) return { color: '#8b5cf6', label: 'On route' };
  if (stage.includes('collected')) return { color: '#22d3ee', label: 'Collected' };
  if (stage.includes('ready')) return { color: '#facc15', label: 'Ready' };
  if (stage.includes('pack')) return { color: '#fb923c', label: 'Packing' };
  return { color: '#60a5fa', label: approvalRequired(order) ? 'Approval' : 'Waiting' };
}

function pointStyle(point = {}) {
  return { left: `${point.x}%`, top: `${point.y}%` };
}

function LiveOperationsMap({ stores = [], orders = [], drivers = [], selectedStore, onSelectStore }) {
  const visibleStores = stores.length ? stores : [];
  const routeOrders = orders
    .filter((order) => !isDeliveredLike(order))
    .slice(0, 8);
  const activeDrivers = drivers.filter((driver) => ['online', 'assigned', 'on route', 'on_route', 'at store'].includes(String(driver.status || driver.state || '').toLowerCase())).slice(0, 8);

  return <section style={mapPanelStyle}>
    <style>{`
      @keyframes linkRoutePulse { 0% { stroke-dashoffset: 36; opacity: .38; } 50% { opacity: .95; } 100% { stroke-dashoffset: 0; opacity: .5; } }
      @keyframes linkNodePulse { 0%, 100% { transform: translate(-50%, -50%) scale(1); } 50% { transform: translate(-50%, -50%) scale(1.12); } }
      @keyframes linkDriverMove { 0%, 100% { transform: translate(-50%, -50%) translateY(0); } 50% { transform: translate(-50%, -50%) translateY(-4px); } }
    `}</style>
    <div style={panelHeaderRowStyle}>
      <div>
        <h3 style={panelTitleStyle}>Live Operations Map</h3>
        <div style={mapLegendStyle}>
          <span style={mapLegendItemStyle}><i style={{ ...mapLegendDotStyle, background: '#3b82f6' }} />Waiting</span>
          <span style={mapLegendItemStyle}><i style={{ ...mapLegendDotStyle, background: '#f59e0b' }} />Packing</span>
          <span style={mapLegendItemStyle}><i style={{ ...mapLegendDotStyle, background: '#eab308' }} />Ready</span>
          <span style={mapLegendItemStyle}><i style={{ ...mapLegendDotStyle, background: '#22d3ee' }} />Collected</span>
          <span style={mapLegendItemStyle}><i style={{ ...mapLegendDotStyle, background: '#8b5cf6' }} />On the way</span>
          <span style={mapLegendItemStyle}><i style={{ ...mapLegendDotStyle, background: '#22c55e' }} />Completed</span>
        </div>
      </div>
      <div style={mapLivePillStyle}>● Live network</div>
    </div>
    <div style={mapCanvasStyle}>
      <div style={mapGridOverlayStyle} />
      <div style={mapRegionLabelStyle}>CAPE TOWN</div>
      <div style={{ ...mapRegionSmallLabelStyle, left: '16%', top: '38%' }}>SEA POINT</div>
      <div style={{ ...mapRegionSmallLabelStyle, left: '54%', top: '28%' }}>PAROW</div>
      <div style={{ ...mapRegionSmallLabelStyle, left: '35%', top: '72%' }}>DISTRICT SIX</div>
      <div style={{ ...mapRegionSmallLabelStyle, left: '70%', top: '50%' }}>FULFILMENT GRID</div>
      <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={mapSvgStyle}>
        {routeOrders.map((order, index) => {
          const storePoint = mapPointForStore(orderStore(order), index);
          const customerPoint = mapPointForOrder(order, index);
          const color = mapRouteColor(index);
          const midX = (storePoint.x + customerPoint.x) / 2;
          const midY = Math.min(storePoint.y, customerPoint.y) - 8;
          return <path
            key={order.id || index}
            d={`M ${storePoint.x} ${storePoint.y} Q ${midX} ${midY} ${customerPoint.x} ${customerPoint.y}`}
            fill="none"
            stroke={color}
            strokeWidth="0.62"
            strokeLinecap="round"
            strokeDasharray="3 3"
            style={{ animation: `linkRoutePulse ${2.8 + (index % 3) * 0.6}s linear infinite`, filter: `drop-shadow(0 0 4px ${color})` }}
          />;
        })}
      </svg>

      {routeOrders.map((order, index) => {
        const customerPoint = mapPointForOrder(order, index);
        const tone = mapStageTone(order);
        return <button
          key={`customer-${order.id || index}`}
          type="button"
          style={{ ...mapCustomerMarkerStyle, ...pointStyle(customerPoint), borderColor: tone.color, boxShadow: `0 0 18px ${tone.color}77` }}
          title={`#${orderNumberText(order)} · ${customerName(order)} · ${tone.label}`}
        >
          <span style={{ background: tone.color }} />
        </button>;
      })}

      {visibleStores.map((row, index) => {
        const pos = mapPointForStore(row.store, index);
        const tone = row.health?.tone === 'risk' ? '#fb7185' : row.health?.tone === 'watch' ? '#fbbf24' : '#34d399';
        return <button key={row.store} type="button" onClick={() => onSelectStore?.(row.store)} style={{ ...mapStoreMarkerStyle, ...pointStyle(pos), borderColor: tone, boxShadow: `0 0 30px ${tone}55`, ...(selectedStore === row.store ? { transform: 'translate(-50%, -50%) scale(1.12)' } : {}) }}>
          <span style={{ ...mapStoreIconStyle, color: tone }}>⌂</span>
          <strong>{row.store}</strong>
          <small>{row.active} active · {row.capacity}%</small>
          <i style={{ ...mapStoreHealthRingStyle, borderColor: tone }} />
        </button>;
      })}

      {activeDrivers.length ? activeDrivers.map((driver, index) => {
        const related = orders.find((order) => driverMatchesOrder(driver, order));
        const storePoint = mapPointForStore(related ? orderStore(related) : (driver.site || driver.store || driver.assignedStore), index);
        const customerPoint = related ? mapPointForOrder(related, index) : { x: storePoint.x + 6 + (index % 2) * 4, y: storePoint.y - 5 + (index % 3) * 4 };
        const x = (storePoint.x * 0.45) + (customerPoint.x * 0.55);
        const y = (storePoint.y * 0.45) + (customerPoint.y * 0.55);
        const color = mapRouteColor(index);
        return <button
          key={driver.id || driver.name || index}
          type="button"
          style={{ ...mapDriverMarkerStyle, left: `${x}%`, top: `${y}%`, borderColor: color, boxShadow: `0 0 22px ${color}aa`, animation: 'linkDriverMove 2.2s ease-in-out infinite' }}
          title={`${driver.name || driver.fullName || 'Driver'} · ${related ? `#${orderNumberText(related)}` : 'available'}`}
        >
          <span>▰</span>
        </button>;
      }) : <div style={mapNoDriversStyle}>No live driver movement yet</div>}

      <div style={mapOverlayStatsStyle}>
        <span><strong>{routeOrders.length}</strong> live routes</span>
        <span><strong>{activeDrivers.length}</strong> drivers</span>
        <span><strong>{routeOrders.filter((order) => attentionStageMinutes(order, Date.now()) >= 20).length}</strong> delayed</span>
      </div>
    </div>
  </section>;
}

function liveOrderStageKey(order = {}) {
  const raw = order.raw || {};
  const stage = String(order.stage || raw.stage || raw.status || raw.subStatus || '').toLowerCase().trim();
  const rawStage = String(raw.stage || raw.status || raw.subStatus || '').toLowerCase().trim();
  const storeStatus = String(order.storeStatus || raw.storeStatus || raw.fulfilment?.storeStatus || raw.fulfillment?.storeStatus || '').toLowerCase().trim();
  const driverStatus = String(raw.driverStatus || raw.fulfilment?.driverStatus || raw.fulfillment?.driverStatus || '').toLowerCase().trim();
  const timings = order.timings || raw.timings || {};

  if (isDeliveredLike(order) || ['delivered', 'complete', 'completed'].includes(stage) || ['delivered', 'complete', 'completed'].includes(rawStage)) return 'delivered';
  if (['out_for_delivery', 'on_the_way', 'on route', 'on_route', 'delivery'].includes(stage) || ['out_for_delivery', 'on_the_way', 'on route', 'on_route', 'delivery'].includes(rawStage) || ['on_route', 'out_for_delivery'].includes(driverStatus)) return 'on_the_way';
  if (['collected', 'picked_up', 'driver_collected', 'handover_complete'].includes(stage) || ['collected', 'picked_up', 'driver_collected', 'handover_complete'].includes(rawStage) || timings.collectedAt || timings.driverPickedUpAt) return 'collected';
  if (['ready_for_driver', 'ready', 'driver_assigned'].includes(stage) || ['ready_for_driver', 'ready', 'driver_assigned'].includes(rawStage) || ['ready', 'ready_for_driver'].includes(storeStatus) || timings.readyForDriverAt || timings.packedAt) return 'ready';
  if (['packing', 'preparing', 'store_packing'].includes(stage) || ['packing', 'preparing', 'store_packing', 'accepted'].includes(rawStage) || ['packing', 'preparing', 'accepted'].includes(storeStatus) || timings.packingStartedAt || timings.storeAcceptedAt) return 'packing';
  return 'received';
}

function OrderFlowPanel({ orders = [], now }) {
  const countStage = (key) => orders.filter((order) => liveOrderStageKey(order) === key).length;
  const maxStageCount = Math.max(1, ...['received', 'packing', 'ready', 'collected', 'on_the_way', 'delivered'].map(countStage));
  const stages = [
    { key: 'received', label: 'Received', value: countStage('received'), tone: '#60a5fa', icon: '▣' },
    { key: 'packing', label: 'Packing', value: countStage('packing'), tone: '#fb923c', icon: '⌂' },
    { key: 'ready', label: 'Ready', value: countStage('ready'), tone: '#facc15', icon: '⌂' },
    { key: 'collected', label: 'Collected', value: countStage('collected'), tone: '#22d3ee', icon: '⌁' },
    { key: 'on_the_way', label: 'On the way', value: countStage('on_the_way'), tone: '#8b5cf6', icon: '⇢' },
    { key: 'delivered', label: 'Delivered', value: countStage('delivered'), tone: '#34d399', icon: '✓' },
  ];
  return <section style={flowPanelStyle}>
    <div style={panelHeaderRowStyle}><h3 style={panelTitleStyle}>Order Flow</h3><span style={miniSelectPillStyle}>Today</span></div>
    <div style={flowTimelineStyle}>
      {stages.map((stage) => (
        <div key={stage.key} style={flowTimelineRowStyle}>
          <div style={{ ...flowTimelineIconStyle, color: stage.tone, borderColor: `${stage.tone}55`, background: `${stage.tone}22` }}>{stage.icon}</div>
          <div style={flowTimelineTextStyle}>
            <strong>{stage.value}</strong>
            <span>{stage.label}</span>
          </div>
          <div style={flowConnectorStyle} aria-hidden="true">
            <i style={{ ...flowConnectorTrackStyle }} />
            <i style={{ ...flowConnectorLineStyle, width: `${Math.max(8, Math.round((stage.value / maxStageCount) * 100))}%`, background: `linear-gradient(90deg, ${stage.tone}, transparent)` }} />
            <b style={{ ...flowConnectorNodeStyle, background: stage.value ? stage.tone : 'rgba(148,163,184,.22)', boxShadow: stage.value ? `0 0 12px ${stage.tone}aa` : 'none' }} />
          </div>
        </div>
      ))}
    </div>
    <div style={flowAveragesStyle}>
      <span>Avg packing <strong>{averageStageMinutes(orders, ['packing'], now)}</strong></span>
      <span>Avg ready <strong>{averageStageMinutes(orders, ['ready_for_driver', 'ready'], now)}</strong></span>
      <span>Avg delivery <strong>{averageStageMinutes(orders, ['out_for_delivery', 'on_the_way', 'delivery'], now)}</strong></span>
    </div>
  </section>;
}

function MiniCommandPanel({ title, action, children }) {
  return <section style={miniPanelStyle}>
    <div style={miniPanelHeaderStyle}><h3>{title}</h3><button type="button">{action}</button></div>
    <div style={{ display: 'grid', gap: 9 }}>{children}</div>
  </section>;
}

function SlaPerformanceCard({ snapshot }) {
  return <section style={performanceCardStyle}>
    <div style={panelHeaderRowStyle}><h3 style={panelTitleStyle}>SLA Performance</h3><span style={miniSelectPillStyle}>Today</span></div>
    <div style={donutWrapStyle}><div style={{ ...donutStyle, background: `conic-gradient(#34d399 0 ${snapshot.onTimePercent}%, rgba(251,113,133,.75) ${snapshot.onTimePercent}% 100%)` }}><strong>{snapshot.onTimePercent}%</strong><span>On Time</span></div></div>
  </section>;
}

function SitePerformanceCard({ rows = [], onSelectStore }) {
  return <section style={performanceCardStyle}>
    <div style={panelHeaderRowStyle}><h3 style={panelTitleStyle}>Site Performance</h3><span style={miniSelectPillStyle}>Today</span></div>
    <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
      {rows.slice(0, 5).map((row) => <button key={row.store} type="button" onClick={() => onSelectStore?.(row.store)} style={sitePerfRowStyle}>
        <span>{row.store}</span><i style={sitePerfBarStyle}><b style={{ display: 'block', height: '100%', borderRadius: 999, background: 'linear-gradient(90deg, #38bdf8, #34d399)', width: `${row.capacity}%` }} /></i><strong>{row.capacity}%</strong>
      </button>)}
    </div>
  </section>;
}

function TopDriversCard({ drivers = [] }) {
  return <section style={performanceCardStyle}>
    <div style={panelHeaderRowStyle}><h3 style={panelTitleStyle}>Top Drivers</h3><span style={miniSelectPillStyle}>Today</span></div>
    <div style={{ display: 'grid', gap: 9, marginTop: 12 }}>
      {drivers.slice(0, 5).map((driver, index) => <div key={driver.id || driver.name || index} style={topDriverRowStyle}>
        <span>{index + 1}</span><strong>{driver.name}</strong><small>{driver.completed} deliveries</small><b>{driver.score}%</b>
      </div>)}
      {!drivers.length ? <div style={miniEmptyStyle}>No drivers yet.</div> : null}
    </div>
  </section>;
}

function OverviewBottomMetrics({ siteRows = [], drivers = [], snapshot = {} }) {
  const activeSites = siteRows.filter((row) => Number(row.active || 0) > 0).length;
  const totalSites = siteRows.length;
  const activeDrivers = drivers.filter((driver) => Number(driver.activeRuns || 0) > 0).length;
  const onlineDrivers = drivers.filter((driver) => String(driver.status || '').toLowerCase() === 'online').length;
  const utilization = totalSites ? Math.round((activeSites / totalSites) * 100) : 0;
  const avgDeliveryValues = drivers.map((driver) => driver.avgDelivery).filter((value) => Number.isFinite(value));
  const avgDelivery = avgDeliveryValues.length ? fmtMins(Math.round(avgDeliveryValues.reduce((sum, value) => sum + value, 0) / avgDeliveryValues.length)) : '—';
  const systemStatus = Number(snapshot.riskCount || 0) ? 'Needs attention' : 'All systems operational';
  const metrics = [
    { icon: '▣', label: 'Active sites', value: `${activeSites} / ${totalSites}` },
    { icon: '◉', label: 'Active drivers', value: activeDrivers },
    { icon: '●', label: 'Online drivers', value: onlineDrivers },
    { icon: '◌', label: 'Utilization', value: `${utilization}%` },
    { icon: '◷', label: 'Avg. delivery', value: avgDelivery },
    { icon: '▤', label: 'Live orders', value: snapshot.activeOrders || 0 },
    { icon: '☘', label: 'CO₂ saved', value: `${Math.max(0, Number(snapshot.completedToday || 0) * 2)} kg` },
    { icon: '●', label: 'System status', value: systemStatus },
  ];
  return <section style={overviewBottomMetricsStyle}>
    {metrics.map((metric) => <div key={metric.label} style={overviewBottomMetricStyle}>
      <span style={overviewBottomMetricIconStyle}>{metric.icon}</span>
      <div>
        <div style={overviewBottomMetricLabelStyle}>{metric.label}</div>
        <strong style={overviewBottomMetricValueStyle}>{metric.value}</strong>
      </div>
    </div>)}
  </section>;
}


function CommandSearchPanel({ query, results = [], onOpen, activeIndex = 0 }) {
  const grouped = results.reduce((acc, item) => {
    acc[item.group] = acc[item.group] || [];
    acc[item.group].push(item);
    return acc;
  }, {});
  const groups = Object.keys(grouped);
  return (
    <div className="link-command-search-panel" style={commandPanelStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <div>
          <div style={eyebrow}>Command search</div>
          <strong>“{query}”</strong>
        </div>
        <span style={commandCountStyle}>{results.length} result{results.length === 1 ? '' : 's'}</span>
      </div>
      {results.length ? groups.map((group) => (
        <div key={group} style={{ marginTop: 14 }}>
          <div style={commandGroupStyle}>{group}</div>
          <div style={{ display: 'grid', gap: 8, marginTop: 8 }}>
            {grouped[group].map((item, itemIndex) => (
              <button key={item.key} type="button" style={{ ...commandResultStyle, ...(activeIndex === itemIndex ? { borderColor: '#5ee7ff', boxShadow: '0 0 0 1px rgba(94,231,255,0.5)' } : {}) }} onClick={() => onOpen(item)}>
                <div>
                  <strong>{item.label}</strong>
                  <div style={{ marginTop: 4, opacity: 0.7, lineHeight: 1.35 }}>{item.detail}</div>
                </div>
                <span style={commandMetaStyle}>{item.meta}</span>
              </button>
            ))}
          </div>
        </div>
      )) : <div style={commandEmptyStyle}>No command matches yet. Try an order number, customer, phone, driver, site, COD, swap, or delay.</div>}
    </div>
  );
}

function TotalsCard({ order, paidTotal, liveTotal, balance }) {
  const packSubtotal = livePackSubtotal(order);
  const delivery = deliveryFee(order);
  const paidByCard = isCardPayment(order);
  return <PanelCard title="Totals">
    <TotalsRow label="Original paid/order total" value={money(paidTotal)} />
    <TotalsRow label="Live pack sub-total" value={money(packSubtotal)} />
    <TotalsRow label="Delivery" value={money(delivery)} />
    <div style={grandTotalStyle}>
      <span>{paidByCard ? 'Swap adjusted total' : 'Total to collect'}</span>
      <strong>{money(liveTotal)}</strong>
    </div>
    <TotalsRow label="Payment" value={paymentLabel(order)} />
    {(originalItems(order).some((item, idx) => isUnavailable(order, item, idx)) || additionItems(order).length > 0) ? <div style={{ ...balanceStyle, ...(balance > 0 ? collectStyle : balance < 0 ? refundStyle : {}) }}>
      <span>{balance === 0 ? 'Difference' : balance > 0 ? 'Customer owes' : 'We owe customer'}</span>
      <strong>{balance < 0 ? `-${money(Math.abs(balance))}` : money(balance)}</strong>
    </div> : null}
  </PanelCard>;
}

function TotalsRow({ label, value }) {
  return <div style={totalsRowStyle}><span>{label}</span><strong>{value}</strong></div>;
}

function OrderInfo({ label, value }) {
  return <div><div style={{ opacity: 0.58, fontSize: 12 }}>{label}</div><div style={{ marginTop: 6, fontWeight: 800 }}>{value || '—'}</div></div>;
}

function SectionTitle({ children }) {
  return <div style={sectionTitleStyle}>{children}</div>;
}

function PanelCard({ title, children, style }) {
  return <div style={{ ...cardStyle, ...(style || {}) }}>{title ? <SectionTitle>{title}</SectionTitle> : null}{children}</div>;
}

function Metric({ title, value }) {
  return <div style={metricStyle}><div style={{ opacity: 0.6, fontSize: 12 }}>{title}</div><div style={{ marginTop: 8, fontSize: 26, fontWeight: 800 }}>{value}</div></div>;
}

function InfoLine({ label, value }) {
  return <div style={{ marginTop: 12, opacity: 0.78 }}><strong>{label}:</strong> {value || '—'}</div>;
}


function TimelineStageCard({ stage, compact = false }) {
  const toneStyle = stage?.tone === 'healthy' ? timelineStageHealthyStyle : stage?.tone === 'delayed' ? timelineStageDelayedStyle : timelineStageCriticalStyle;
  return <div style={{ ...timelineStageCardStyle, ...toneStyle, ...(compact ? { marginBottom: 14 } : {}) }}>
    <div>
      <div style={sectionTitleStyle}>Current stage</div>
      <div style={{ fontSize: compact ? 20 : 24, fontWeight: 900 }}>{stage?.label || 'Unknown stage'}</div>
      <div style={{ marginTop: 6, opacity: 0.72 }}>{stage?.detail || 'Operational stage detection'}</div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: compact ? 22 : 28, fontWeight: 900 }}>{stage?.time || '—'}</div>
      <div style={{ marginTop: 6, opacity: 0.72 }}>{stage?.breachText || '—'}</div>
    </div>
  </div>;
}

function TimelineStep({ step }) {
  const toneStyle = step.tone === 'healthy' ? timelineStepHealthyStyle : step.tone === 'delayed' ? timelineStepDelayedStyle : step.tone === 'critical' ? timelineStepCriticalStyle : timelineStepPendingStyle;
  return <div style={{ ...timelineStepStyle, ...toneStyle }}>
    <div style={{ fontWeight: 900 }}>{step.label}</div>
    <div style={{ marginTop: 6, opacity: 0.72 }}>{step.note}</div>
    <div style={{ marginTop: 8, color: '#9defff', fontWeight: 800 }}>{step.time}</div>
  </div>;
}

function TimelineRow({ item }) {
  return <div style={timelineRowStyle}>
    <div><strong>{item.title}</strong><div style={{ marginTop: 5, opacity: 0.62 }}>{formatClock(item.at)}</div></div>
    <div style={{ color: '#7ee7ff', fontWeight: 800 }}>{item.duration}</div>
  </div>;
}

function ChatBubble({ chat, compact = false }) {
  const mine = String(chat.actorRole || '').toLowerCase().includes('online');
  return <div style={{ ...chatBubbleStyle, ...(mine ? myChatBubbleStyle : {}), ...(compact ? { padding: 12 } : {}) }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
      <strong>{chat.actorName || chat.role || 'Link user'}</strong>
      <span style={{ opacity: 0.58 }}>{chat.actorRole || ''} · {formatClock(chat.createdAt)}</span>
    </div>
    {chat.text ? <p style={{ margin: '8px 0 0', lineHeight: 1.45 }}>{chat.text}</p> : null}
    {chat.attachmentUrl ? <a href={chat.attachmentUrl} target="_blank" rel="noreferrer" style={attachmentStyle}>Photo attachment</a> : null}
  </div>;
}

function EvidenceCard({ item, onPreview }) {
  return <button type="button" onClick={() => item.url && onPreview?.(item)} style={{ ...evidenceStyle, textAlign: 'left', color: '#fff', cursor: item.url ? 'pointer' : 'default' }}>
    <strong>{item.title}</strong>
    <div style={{ marginTop: 8, opacity: 0.65 }}>{formatClock(item.time)}</div>
    <div style={proofBoxStyle}>{item.url ? <img src={item.url} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} /> : 'No proof yet'}</div>
  </button>;
}


function OverviewMetric({ title, value }) {
  return (
    <div style={metricStyle}>
      <div style={{ opacity: 0.6, fontSize: 12 }}>{title}</div>
      <div style={{ marginTop: 8, fontSize: 24, fontWeight: 800 }}>{value}</div>
    </div>
  );
}


function AttentionSummaryPill({ label, value, tone, active, onClick }) {
  const toneStyle = tone === 'critical' ? attentionCriticalPillStyle : tone === 'delayed' ? attentionDelayedPillStyle : tone === 'operational' ? attentionOperationalPillStyle : attentionAllPillStyle;
  return (
    <button type="button" onClick={onClick} style={{ ...attentionSummaryPillStyle, ...toneStyle, ...(active ? activeAttentionSummaryPillStyle : {}) }}>
      <span>{label}</span>
      <strong>{value}</strong>
    </button>
  );
}

function OrderRiskStrip({ items = [] }) {
  if (!items.length) return null;
  return (
    <div style={orderRiskStripStyle}>
      {items.map((item) => (
        <span key={item.key} style={{ ...orderRiskPillStyle, ...(item.severity === 'critical' ? attentionCriticalPillStyle : item.severity === 'delayed' ? attentionDelayedPillStyle : attentionOperationalPillStyle) }}>
          {item.type}
        </span>
      ))}
    </div>
  );
}

function EmptyPanel({ text }) {
  return <div style={{ ...cardStyle, opacity: 0.75 }}>{text}</div>;
}

function StatusPill({ label, tone }) {
  const style = tone === 'green' ? greenPillStyle : tone === 'amber' ? amberPillStyle : bluePillStyle;
  return <span style={style}>{label}</span>;
}


const commandPanelStyle = {
  position: 'fixed',
  zIndex: 8500,
  top: 112,
  left: 'clamp(360px, 44vw, 920px)',
  transform: 'translateX(-50%)',
  width: 'min(560px, calc(100vw - 420px))',
  maxHeight: 'min(70vh, 620px)',
  overflow: 'auto',
  borderRadius: 22,
  padding: 16,
  background: 'linear-gradient(180deg, rgba(10,23,40,0.98), rgba(5,13,26,0.98))',
  border: '1px solid rgba(90,200,250,0.24)',
  boxShadow: '0 24px 80px rgba(0,0,0,0.55), 0 0 40px rgba(90,200,250,0.10)',
};
const commandCountStyle = { borderRadius: 999, padding: '6px 10px', background: 'rgba(90,200,250,0.12)', color: '#9defff', fontSize: 12, fontWeight: 900 };
const commandGroupStyle = { color: '#67e8f9', fontSize: 11, fontWeight: 900, letterSpacing: '0.22em', textTransform: 'uppercase' };
const commandResultStyle = { width: '100%', borderRadius: 15, padding: 13, background: '#101d31', border: '1px solid rgba(255,255,255,0.06)', color: '#fff', display: 'flex', justifyContent: 'space-between', gap: 12, textAlign: 'left', cursor: 'pointer' };
const commandMetaStyle = { flex: '0 0 auto', alignSelf: 'center', borderRadius: 999, padding: '5px 8px', background: 'rgba(255,255,255,0.06)', color: '#c9f6ff', fontSize: 11, fontWeight: 900, textTransform: 'capitalize' };
const commandEmptyStyle = { marginTop: 14, borderRadius: 15, padding: 14, background: '#101d31', opacity: 0.72 };


const commandCenterStyle = { gridColumn: '1 / -1', display: 'grid', gap: 12 };
const networkKpiGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(5, minmax(150px, 1fr))', gap: 10 };
const networkKpiCardStyle = { minHeight: 86, borderRadius: 18, padding: 14, display: 'flex', gap: 12, alignItems: 'center', border: '1px solid rgba(94,231,255,.14)', background: 'linear-gradient(180deg, rgba(10,24,44,.92), rgba(5,13,25,.94))', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.04), 0 18px 44px rgba(0,0,0,.22)' };
const networkKpiIconStyle = { width: 46, height: 46, borderRadius: 14, display: 'grid', placeItems: 'center', fontWeight: 900, fontSize: 20, flex: '0 0 auto' };
const networkKpiTitleStyle = { textTransform: 'uppercase', letterSpacing: '.13em', color: '#93a8c6', fontSize: 10, fontWeight: 900 };
const networkKpiValueStyle = { marginTop: 3, fontSize: 25, lineHeight: 1, fontWeight: 950 };
const networkKpiDeltaStyle = { marginTop: 5, color: '#7dd3fc', fontSize: 11, fontWeight: 800 };
const kpiToneStyles = {
  blue: { card: { borderColor: 'rgba(59,130,246,.24)' }, icon: { color: '#93c5fd', background: 'rgba(59,130,246,.16)' } },
  green: { card: { borderColor: 'rgba(34,197,94,.24)' }, icon: { color: '#86efac', background: 'rgba(34,197,94,.14)' } },
  amber: { card: { borderColor: 'rgba(251,146,60,.28)' }, icon: { color: '#fdba74', background: 'rgba(251,146,60,.15)' } },
  red: { card: { borderColor: 'rgba(248,113,113,.30)' }, icon: { color: '#fca5a5', background: 'rgba(248,113,113,.16)' } },
  purple: { card: { borderColor: 'rgba(139,92,246,.28)' }, icon: { color: '#c4b5fd', background: 'rgba(139,92,246,.16)' } },
};
const operationsGridStyle = { display: 'grid', gridTemplateColumns: 'minmax(420px, 1.45fr) minmax(210px, .52fr) minmax(270px, .66fr)', gap: 12, alignItems: 'stretch' };
const mapPanelStyle = { minHeight: 392, borderRadius: 20, border: '1px solid rgba(94,231,255,.16)', background: 'linear-gradient(180deg, rgba(9,22,40,.92), rgba(3,10,21,.95))', boxShadow: '0 20px 58px rgba(0,0,0,.28), inset 0 1px 0 rgba(255,255,255,.035)', padding: 14, overflow: 'hidden' };
const panelHeaderRowStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 };
const panelTitleStyle = { margin: 0, fontSize: 16, fontWeight: 900, letterSpacing: '-.02em' };
const mapLegendStyle = { marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 12, color: '#b8c7df', fontSize: 11 };
const mapLegendItemStyle = { display: 'inline-flex', alignItems: 'center', gap: 6 };
const mapLegendDotStyle = { display: 'inline-block', width: 9, height: 9, borderRadius: 99, boxShadow: '0 0 10px currentColor' };
const mapUtilityButtonStyle = { width: 34, height: 34, borderRadius: 12, border: '1px solid rgba(148,163,184,.16)', background: 'rgba(2,8,23,.55)', color: '#dffbff' };
const mapCanvasStyle = { position: 'relative', height: 310, marginTop: 14, borderRadius: 18, overflow: 'hidden', border: '1px solid rgba(94,231,255,.10)', background: 'radial-gradient(circle at 18% 30%, rgba(59,130,246,.22), transparent 26%), radial-gradient(circle at 72% 42%, rgba(139,92,246,.18), transparent 28%), radial-gradient(circle at 44% 68%, rgba(34,211,238,.12), transparent 22%), linear-gradient(135deg, rgba(7,27,50,.94), rgba(2,8,23,.96))' };
const mapGridOverlayStyle = { position: 'absolute', inset: 0, opacity: .45, backgroundImage: 'linear-gradient(rgba(94,231,255,.07) 1px, transparent 1px), linear-gradient(90deg, rgba(94,231,255,.07) 1px, transparent 1px)', backgroundSize: '52px 52px' };
const mapSvgStyle = { position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 2 };
const mapLivePillStyle = { borderRadius: 999, padding: '7px 10px', border: '1px solid rgba(52,211,153,.22)', background: 'rgba(6,78,59,.16)', color: '#9bffc3', fontSize: 11, fontWeight: 900 };
const mapRegionLabelStyle = { position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', fontSize: 48, fontWeight: 950, letterSpacing: '.18em', color: 'rgba(148,163,184,.055)', pointerEvents: 'none', zIndex: 1 };
const mapRegionSmallLabelStyle = { position: 'absolute', color: 'rgba(203,213,225,.18)', fontSize: 10, letterSpacing: '.14em', fontWeight: 900, pointerEvents: 'none', zIndex: 1 };
const mapRouteStyle = { position: 'absolute', height: 0, borderTop: '2px solid rgba(94,231,255,.45)', borderRadius: 999, filter: 'drop-shadow(0 0 8px rgba(94,231,255,.55))' };
const mapStoreMarkerStyle = { position: 'absolute', zIndex: 5, transform: 'translate(-50%, -50%)', display: 'grid', gap: 1, minWidth: 116, padding: '10px 12px', borderRadius: 17, border: '1px solid rgba(94,231,255,.25)', background: 'linear-gradient(180deg, rgba(5,16,32,.94), rgba(4,10,22,.88))', color: '#fff', textAlign: 'left', cursor: 'pointer', transition: 'transform .18s ease, border-color .18s ease' };
const mapStoreIconStyle = { fontSize: 13, lineHeight: 1 };
const mapStoreHealthRingStyle = { position: 'absolute', inset: -5, borderRadius: 21, border: '1px solid rgba(255,255,255,.2)', opacity: .42, pointerEvents: 'none', animation: 'linkNodePulse 2.8s ease-in-out infinite' };
const mapDriverMarkerStyle = { position: 'absolute', zIndex: 6, transform: 'translate(-50%, -50%)', width: 28, height: 20, borderRadius: 9, display: 'grid', placeItems: 'center', color: '#fff', background: 'rgba(139,92,246,.88)', border: '1px solid rgba(255,255,255,.25)', boxShadow: '0 0 18px rgba(139,92,246,.55)', cursor: 'pointer' };
const mapCustomerMarkerStyle = { position: 'absolute', zIndex: 4, transform: 'translate(-50%, -50%)', width: 18, height: 18, borderRadius: 999, border: '1px solid rgba(255,255,255,.24)', background: 'rgba(2,8,23,.82)', display: 'grid', placeItems: 'center', cursor: 'pointer', animation: 'linkNodePulse 3.1s ease-in-out infinite' };
const mapNoDriversStyle = { position: 'absolute', right: 14, bottom: 14, zIndex: 4, borderRadius: 999, padding: '7px 10px', background: 'rgba(15,23,42,.72)', color: '#94a3b8', fontSize: 11, fontWeight: 800, border: '1px solid rgba(148,163,184,.12)' };
const mapOverlayStatsStyle = { position: 'absolute', left: 14, bottom: 14, zIndex: 4, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderRadius: 999, padding: '8px 11px', background: 'rgba(3,10,21,.76)', color: '#c9f6ff', border: '1px solid rgba(94,231,255,.16)', boxShadow: '0 0 24px rgba(34,211,238,.10)', fontSize: 11 };
const mapOrderDotStyle = { position: 'absolute', width: 9, height: 9, borderRadius: 99, background: '#22d3ee', boxShadow: '0 0 14px rgba(34,211,238,.85)' };
const flowPanelStyle = { borderRadius: 20, border: '1px solid rgba(94,231,255,.14)', background: 'linear-gradient(180deg, rgba(9,22,40,.90), rgba(3,10,21,.94))', padding: 14, minHeight: 392 };
const flowTimelineStyle = { position: 'relative', display: 'grid', gap: 8, marginTop: 16, paddingLeft: 2 };
const flowTimelineRowStyle = { display: 'grid', gridTemplateColumns: '30px minmax(72px, 88px) minmax(70px, 1fr)', gap: 9, alignItems: 'center', color: '#dffbff', minHeight: 46, minWidth: 0 };
const flowTimelineIconStyle = { width: 28, height: 28, borderRadius: 999, display: 'grid', placeItems: 'center', border: '1px solid rgba(56,189,248,.28)', fontWeight: 900, boxShadow: '0 0 18px currentColor', flexShrink: 0 };
const flowTimelineTextStyle = { display: 'grid', gap: 2, minWidth: 0 };
const flowConnectorStyle = { position: 'relative', minWidth: 64, height: 28, opacity: .88, overflow: 'hidden' };
const flowConnectorTrackStyle = { position: 'absolute', left: 0, right: 8, top: 13, height: 2, borderRadius: 999, background: 'rgba(148,163,184,.16)' };
const flowConnectorLineStyle = { position: 'absolute', left: 0, top: 13, height: 2, borderRadius: 999 };
const flowConnectorNodeStyle = { position: 'absolute', right: 0, top: 9, width: 9, height: 9, borderRadius: 999, background: 'rgba(148,163,184,.22)', transform: 'rotate(45deg)' };
const flowPercentStyle = { justifySelf: 'end', color: '#c9f6ff', fontWeight: 900, fontSize: 11, minWidth: 28, textAlign: 'right', overflow: 'hidden' };
const flowAveragesStyle = { marginTop: 12, paddingTop: 10, borderTop: '1px solid rgba(148,163,184,.10)', display: 'grid', gap: 7, color: '#94a3b8', fontSize: 11 };
const miniSelectPillStyle = { borderRadius: 999, padding: '6px 9px', color: '#cbd5e1', background: 'rgba(15,23,42,.72)', border: '1px solid rgba(148,163,184,.12)', fontSize: 11, fontWeight: 900 };
const commandRightRailStyle = { display: 'grid', gap: 12, alignContent: 'start' };
const miniPanelStyle = { borderRadius: 18, border: '1px solid rgba(94,231,255,.14)', background: 'rgba(7,18,34,.88)', padding: 13, minWidth: 0 };
const miniPanelHeaderStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 10 };
const miniAlertStyle = { borderRadius: 14, border: '1px solid rgba(255,255,255,.08)', padding: 11, color: '#fff', background: 'rgba(15,23,42,.7)', textAlign: 'left', cursor: 'pointer', display: 'grid', gridTemplateColumns: '30px 1fr auto', gap: 10, alignItems: 'center' };
const miniAlertCriticalStyle = { borderColor: 'rgba(251,113,133,.28)', background: 'rgba(127,29,29,.25)' };
const miniAlertDelayedStyle = { borderColor: 'rgba(251,191,36,.25)', background: 'rgba(120,53,15,.22)' };
const miniAlertOperationalStyle = { borderColor: 'rgba(94,231,255,.22)', background: 'rgba(8,47,73,.24)' };
const miniAlertTagStyle = { textTransform: 'uppercase', fontSize: 10, borderRadius: 999, padding: '4px 7px', background: 'rgba(255,255,255,.08)', color: '#dffbff' };
const miniEmptyStyle = { borderRadius: 14, padding: 12, color: '#94a3b8', background: 'rgba(15,23,42,.52)' };
const alertDotIconStyle = { width: 26, height: 26, borderRadius: 999, display: 'grid', placeItems: 'center', border: '1px solid currentColor', fontSize: 12, fontWeight: 900, boxShadow: '0 0 16px currentColor' };
const alertDotCriticalStyle = { color: '#fb7185' };
const alertDotDelayedStyle = { color: '#f59e0b' };
const alertDotOperationalStyle = { color: '#22d3ee' };
const miniAlertTitleRowStyle = { display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' };
const miniAlertBodyStyle = { marginTop: 5, opacity: 0.72, fontSize: 12, lineHeight: 1.35 };
const miniAlertTimeStyle = { opacity: 0.65, fontSize: 11, whiteSpace: 'nowrap' };
const miniAlertTagCriticalStyle = { background: 'rgba(127,29,29,.72)', color: '#fecdd3' };
const miniAlertTagDelayedStyle = { background: 'rgba(120,53,15,.72)', color: '#fed7aa' };
const miniAlertTagOperationalStyle = { background: 'rgba(8,47,73,.72)', color: '#bae6fd' };
const driverActivityMetaStyle = { marginTop: 3, opacity: 0.72, fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 6 };
const driverStatusDotStyle = { width: 7, height: 7, borderRadius: 999, boxShadow: '0 0 10px currentColor' };
const driverActivityTaskStyle = { color: '#cbd5e1', fontSize: 12, textAlign: 'right', maxWidth: 116, lineHeight: 1.25 };
const recentOrderStatusPillStyle = { justifySelf: 'end', borderRadius: 999, padding: '5px 8px', background: 'rgba(59,130,246,.20)', color: '#bfdbfe', fontWeight: 900, fontSize: 11, whiteSpace: 'nowrap' };
const recentOrderMainStyle = { display: 'grid', gap: 3, minWidth: 0 };
const recentOrderNumberStyle = { display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: 1.1 };
const recentOrderStoreStyle = { display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#cbd5e1', fontWeight: 800 };
const recentOrderSideStyle = { display: 'grid', gap: 6, justifyItems: 'end', alignItems: 'center', minWidth: 74 };
const recentOrderTimeStyle = { color: '#7dd3fc', fontStyle: 'normal', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' };

const driverActivityRowStyle = { display: 'grid', gridTemplateColumns: '32px 1fr auto', gap: 10, alignItems: 'center', padding: '8px 0', borderBottom: '1px solid rgba(148,163,184,.08)' };
const driverAvatarMiniStyle = { width: 30, height: 30, borderRadius: 999, display: 'grid', placeItems: 'center', background: 'rgba(94,231,255,.16)', color: '#dffbff', fontWeight: 900 };
const driverScoreMiniStyle = { color: '#86efac', fontWeight: 900 };
const recentOrderRowStyle = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10, alignItems: 'center', border: 0, borderBottom: '1px solid rgba(148,163,184,.08)', background: 'transparent', color: '#dffbff', textAlign: 'left', padding: '10px 0', cursor: 'pointer', width: '100%', minWidth: 0 };
const performanceStripStyle = { display: 'grid', gridTemplateColumns: 'minmax(220px, .8fr) minmax(250px, 1fr) minmax(250px, 1fr)', gap: 12 };
const performanceCardStyle = { minHeight: 150, borderRadius: 18, border: '1px solid rgba(94,231,255,.14)', background: 'linear-gradient(180deg, rgba(9,22,40,.88), rgba(3,10,21,.94))', padding: 14 };
const donutWrapStyle = { display: 'grid', placeItems: 'center', marginTop: 8 };
const donutStyle = { width: 104, height: 104, borderRadius: 999, display: 'grid', placeItems: 'center', color: '#fff', boxShadow: '0 0 30px rgba(34,197,94,.16)' };
const sitePerfRowStyle = { display: 'grid', gridTemplateColumns: '100px 1fr 42px', gap: 10, alignItems: 'center', background: 'transparent', border: 0, color: '#e5f9ff', textAlign: 'left', cursor: 'pointer' };
const sitePerfBarStyle = { height: 6, borderRadius: 999, background: 'rgba(148,163,184,.12)', overflow: 'hidden' };
const topDriverRowStyle = { display: 'grid', gridTemplateColumns: '22px 1fr auto 42px', gap: 9, alignItems: 'center', color: '#dffbff' };

const overviewBottomMetricsStyle = { display: 'grid', gridTemplateColumns: 'repeat(8, minmax(112px, 1fr))', gap: 8, padding: 10, borderRadius: 18, border: '1px solid rgba(94,231,255,.13)', background: 'rgba(3,10,22,.78)', boxShadow: 'inset 0 1px 0 rgba(255,255,255,.035)' };
const overviewBottomMetricStyle = { display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, padding: '9px 10px', borderRadius: 13, background: 'rgba(15,23,42,.42)', border: '1px solid rgba(148,163,184,.08)' };
const overviewBottomMetricIconStyle = { width: 30, height: 30, borderRadius: 11, display: 'grid', placeItems: 'center', background: 'rgba(34,211,238,.12)', border: '1px solid rgba(94,231,255,.16)', color: '#67e8f9', flex: '0 0 auto' };
const overviewBottomMetricLabelStyle = { color: '#94a3b8', fontSize: 11, fontWeight: 800, whiteSpace: 'nowrap' };
const overviewBottomMetricValueStyle = { display: 'block', marginTop: 3, color: '#eaffff', fontSize: 15, fontWeight: 900, whiteSpace: 'nowrap' };

const pageStyle = { minHeight: 'calc(100vh - 150px)', width: '100%', maxWidth: '100%', boxSizing: 'border-box', background: '#07111f', color: '#fff', padding: 'clamp(10px, 1vw, 16px)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(270px, 330px)', gridTemplateRows: 'auto auto', alignItems: 'start', gap: 'clamp(10px, 1vw, 14px)', overflow: 'visible' };
const leftStyle = { display: 'none' };
const centerStyle = { gridColumn: '1 / 2', gridRow: '2 / 3', minWidth: 0, display: 'none', flexDirection: 'column', gap: 12, maxHeight: 'calc(100vh - 205px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: 8, scrollbarGutter: 'stable' };
const rightStyle = { display: 'none', gridColumn: '2 / 3', gridRow: '2 / 3', minWidth: 0, background: '#0b1728', borderRadius: 20, border: '1px solid rgba(255,255,255,0.05)', padding: 'clamp(12px, 1vw, 16px)', maxHeight: 'calc(100vh - 190px)', overflow: 'auto', position: 'sticky', top: 12 };
const ordersPanelStyle = { overflow: 'visible', paddingRight: 8, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 };
const headerCardStyle = { background: '#0b1728', borderRadius: 24, border: '1px solid rgba(255,255,255,0.05)', padding: 22 };
const attentionStyle = { flex: '0 0 190px', borderRadius: 18, padding: 18, background: 'rgba(255,95,109,0.08)', border: '1px solid rgba(255,95,109,0.14)' };

const attentionEngineStyle = { background: '#0b1728', borderRadius: 24, border: '1px solid rgba(255,255,255,0.05)', padding: 18 };
const attentionSummaryStyle = { display: 'flex', gap: 10, flexWrap: 'wrap' };
const attentionSummaryPillStyle = { borderRadius: 999, padding: '9px 13px', display: 'inline-flex', gap: 9, alignItems: 'center', border: '1px solid rgba(255,255,255,0.08)', textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: 11, color: '#fff', cursor: 'pointer' };
const attentionFeedStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 12, marginTop: 16 };
const attentionCardStyle = { width: '100%', textAlign: 'left', borderRadius: 18, padding: 15, color: '#fff', cursor: 'pointer', background: '#101d31', border: '1px solid rgba(255,255,255,0.06)' };
const attentionCriticalStyle = { background: 'rgba(255,95,109,0.08)', border: '1px solid rgba(255,95,109,0.22)' };
const attentionDelayedStyle = { background: 'rgba(255,179,71,0.08)', border: '1px solid rgba(255,179,71,0.20)' };
const attentionOperationalStyle = { background: 'rgba(90,200,250,0.07)', border: '1px solid rgba(90,200,250,0.18)' };
const attentionSeverityPillStyle = { borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.08em' };
const attentionAllPillStyle = { background: 'rgba(255,255,255,0.06)', color: '#d9f6ff' };
const activeAttentionSummaryPillStyle = { boxShadow: '0 0 0 1px rgba(143,233,255,0.28) inset', transform: 'translateY(-1px)' };
const attentionCriticalPillStyle = { background: 'rgba(255,95,109,0.16)', color: '#ffb3bc' };
const attentionDelayedPillStyle = { background: 'rgba(255,179,71,0.14)', color: '#ffd38d' };
const attentionOperationalPillStyle = { background: 'rgba(90,200,250,0.14)', color: '#9defff' };
const attentionEmptyStyle = { marginTop: 16, borderRadius: 18, background: '#101d31', border: '1px solid rgba(255,255,255,0.04)', padding: 16, opacity: 0.72 };
const nextMoveStyle = { marginTop: 16, borderRadius: 18, padding: 14, background: 'linear-gradient(135deg, rgba(90,200,250,0.13), rgba(255,179,71,0.08))', border: '1px solid rgba(143,233,255,0.14)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 14 };
const nextMoveButtonStyle = { flex: '0 0 auto', borderRadius: 999, padding: '11px 15px', background: 'rgba(90,200,250,0.18)', border: '1px solid rgba(90,200,250,0.28)', color: '#fff', fontWeight: 900, cursor: 'pointer' };
const orderRiskStripStyle = { marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 8 };
const orderRiskPillStyle = { borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '0.06em' };
const miniDelayedPillStyle = { borderRadius: 999, padding: '3px 7px', background: 'rgba(255,179,71,0.12)', color: '#ffd38d', fontWeight: 800 };
const miniOperationalPillStyle = { borderRadius: 999, padding: '3px 7px', background: 'rgba(90,200,250,0.12)', color: '#9defff', fontWeight: 800 };
const miniHealthyPillStyle = { borderRadius: 999, padding: '3px 7px', background: 'rgba(61,220,132,0.12)', color: '#9bffc3', fontWeight: 800 };
const siteHealthPillStyle = { flex: '0 0 auto', borderRadius: 999, padding: '5px 8px', fontSize: 10, fontWeight: 1000, textTransform: 'uppercase', letterSpacing: '0.06em' };
const siteHealthyPillStyle = { background: 'rgba(61,220,132,0.14)', color: '#9bffc3', border: '1px solid rgba(61,220,132,0.26)' };
const siteWatchPillStyle = { background: 'rgba(255,179,71,0.14)', color: '#ffd38d', border: '1px solid rgba(255,179,71,0.28)' };
const siteRiskPillStyle = { background: 'rgba(255,95,109,0.16)', color: '#ffb3bc', border: '1px solid rgba(255,95,109,0.30)' };
const siteHealthyCardStyle = { background: 'linear-gradient(135deg, rgba(61,220,132,0.07), rgba(15,28,47,0.98))', border: '1px solid rgba(61,220,132,0.15)' };
const siteWatchCardStyle = { background: 'linear-gradient(135deg, rgba(255,179,71,0.08), rgba(15,28,47,0.98))', border: '1px solid rgba(255,179,71,0.18)' };
const siteRiskCardStyle = { background: 'linear-gradient(135deg, rgba(255,95,109,0.10), rgba(15,28,47,0.98))', border: '1px solid rgba(255,95,109,0.20)' };
const siteCardGridStyle = { marginTop: 12, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8, fontSize: 12 };
const siteCapacitySummaryStyle = { minWidth: 150, borderRadius: 18, padding: '12px 14px', background: 'rgba(3,10,21,0.45)', border: '1px solid rgba(255,255,255,0.06)', display: 'grid', gap: 5, justifyItems: 'start' };

const driverIntelligenceStyle = { background: '#0b1728', borderRadius: 24, border: '1px solid rgba(90,200,250,0.10)', padding: 18 };
const driverMonitorPillStyle = { borderRadius: 999, padding: '8px 12px', background: 'rgba(90,200,250,0.12)', border: '1px solid rgba(90,200,250,0.22)', color: '#9defff', fontSize: 12, fontWeight: 900 };
const driverRadarGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginTop: 16, width: '100%', overflow: 'visible' };
const driverRadarCardStyle = { borderRadius: 18, padding: 16, background: 'linear-gradient(135deg, rgba(16,29,49,0.98), rgba(11,23,40,0.98))', border: '1px solid rgba(255,255,255,0.06)', minWidth: 0, boxShadow: '0 18px 40px rgba(0,0,0,0.16)' };
const driverScorePillStyle = { flex: '0 0 auto', borderRadius: 999, padding: '6px 10px', fontSize: 12, fontWeight: 1000 };
const driverScoreGoodStyle = { background: 'rgba(61,220,132,0.15)', color: '#9bffc3', border: '1px solid rgba(61,220,132,0.30)' };
const driverScoreWatchStyle = { background: 'rgba(255,179,71,0.14)', color: '#ffd38d', border: '1px solid rgba(255,179,71,0.28)' };
const driverScoreRiskStyle = { background: 'rgba(255,95,109,0.16)', color: '#ffb3bc', border: '1px solid rgba(255,95,109,0.30)' };
const driverStatsRowStyle = { marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 9 };
const driverMetricBoxStyle = { borderRadius: 13, padding: '10px 11px', background: 'rgba(3,10,21,0.55)', border: '1px solid rgba(255,255,255,0.045)', minWidth: 0 };
const driverMetricValueStyle = { fontSize: 16, fontWeight: 950, color: '#fff', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
const driverMetricLabelStyle = { marginTop: 3, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.62 };
const driverMiniGridStyle = { marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 };
const driverRiskListStyle = { marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 };
const driverRiskChipStyle = { borderRadius: 999, padding: '5px 8px', background: 'rgba(255,95,109,0.12)', color: '#ffccd2', border: '1px solid rgba(255,95,109,0.20)', fontSize: 11, fontWeight: 900 };
const driverCleanStyle = { marginTop: 12, color: '#9bffc3', fontWeight: 900 };

const topStoreRailStyle = { flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'stretch', gap: 12, overflowX: 'auto', paddingBottom: 2 };
const topStoreRailLabelStyle = { opacity: 0.62, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', flex: '0 0 auto', alignSelf: 'center', marginRight: 2 };
const storeButtonStyle = { flex: '0 0 190px', minWidth: 190, marginTop: 0, borderRadius: 18, padding: 16, background: '#0f1c2f', border: '1px solid rgba(255,255,255,0.04)', color: '#fff', textAlign: 'left', cursor: 'pointer' };
const selectedStoreButtonStyle = { background: 'rgba(90,200,250,0.08)', border: '1px solid rgba(90,200,250,0.16)' };
const orderCardStyle = { background: '#0b1728', borderRadius: 22, border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden', flexShrink: 0 };
const expandedOrderCardStyle = { border: '1px solid rgba(90,200,250,0.2)' };
const orderHeaderButtonStyle = { width: '100%', background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', padding: 22, textAlign: 'left' };
const orderInfoGridStyle = { marginTop: 18, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(88px, 1fr))', gap: 12 };
const orderInfoGridLaptopStyle = { gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' };
const orderInfoGridMobileStyle = { gridTemplateColumns: '1fr 1fr' };
const expandedBodyStyle = { padding: 22, borderTop: '1px solid rgba(255,255,255,0.05)', background: '#0f1c2f', display: 'grid', gap: 16 };
const detailGridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 };
const cardStyle = { borderRadius: 18, background: '#101d31', border: '1px solid rgba(255,255,255,0.04)', padding: 18 };
const sectionTitleStyle = { opacity: 0.62, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12 };
const eyebrow = { opacity: 0.62, fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' };
const delayBadgeStyle = { background: 'rgba(255,95,109,0.12)', color: '#ffb3bc', borderRadius: 999, padding: '10px 14px', height: 'fit-content', fontWeight: 800 };
const greenPillStyle = { borderRadius: 999, padding: '6px 10px', background: 'rgba(74,222,128,0.14)', color: '#9ff7b5', fontSize: 12, fontWeight: 800 };
const amberPillStyle = { borderRadius: 999, padding: '6px 10px', background: 'rgba(255,179,71,0.12)', color: '#ffd38d', fontSize: 12, fontWeight: 800 };
const bluePillStyle = { borderRadius: 999, padding: '6px 10px', background: 'rgba(90,200,250,0.12)', color: '#9defff', fontSize: 12, fontWeight: 800 };
const lineItemStyle = { padding: '13px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', gap: 12 };
const unavailableLineStyle = { opacity: 0.48, textDecoration: 'line-through' };
const unavailablePillStyle = { marginLeft: 8, textDecoration: 'none', borderRadius: 999, padding: '3px 7px', background: 'rgba(255,95,109,0.16)', color: '#ffb3bc' };
const additionLineStyle = { marginTop: 10, borderRadius: 14, padding: 14, background: 'rgba(90,200,250,0.08)' };
const smallActionButton = { borderRadius: 13, padding: '11px 14px', border: '1px solid rgba(90,200,250,0.16)', background: 'rgba(90,200,250,0.08)', color: '#fff', cursor: 'pointer', fontWeight: 800 };
const primaryActionButton = { ...smallActionButton, background: 'linear-gradient(135deg, rgba(90,200,250,0.28), rgba(74,222,128,0.16))' };
const totalsRowStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '9px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' };
const grandTotalStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, padding: '14px 0', fontSize: 20, borderBottom: '1px solid rgba(255,255,255,0.05)' };
const balanceStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 12, borderRadius: 12, padding: 12, background: 'rgba(255,255,255,0.04)' };
const collectStyle = { background: 'rgba(255,179,71,0.1)', color: '#ffd38d' };
const refundStyle = { background: 'rgba(74,222,128,0.1)', color: '#9ff7b5' };
const metricGridStyle = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const metricStyle = { background: '#081423', borderRadius: 14, padding: 14 };
const tabButtonStyle = { borderRadius: 999, padding: '9px 12px', background: '#0f1c2f', border: '1px solid rgba(255,255,255,0.04)', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' };
const activeTabButtonStyle = { background: 'rgba(90,200,250,0.12)', border: '1px solid rgba(90,200,250,0.18)' };

const timelineStageCardStyle = { marginBottom: 16, borderRadius: 18, padding: 16, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.035)', display: 'flex', justifyContent: 'space-between', gap: 16, alignItems: 'center' };
const timelineStageHealthyStyle = { borderColor: 'rgba(74,222,128,0.22)', background: 'rgba(74,222,128,0.07)' };
const timelineStageDelayedStyle = { borderColor: 'rgba(255,179,71,0.24)', background: 'rgba(255,179,71,0.08)' };
const timelineStageCriticalStyle = { borderColor: 'rgba(255,95,109,0.28)', background: 'rgba(255,95,109,0.10)' };
const timelineFlowStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(132px, 1fr))', gap: 10, marginBottom: 4 };
const timelineStepStyle = { borderRadius: 14, padding: 12, border: '1px solid rgba(255,255,255,0.05)', background: '#081423', minHeight: 96 };
const timelineStepHealthyStyle = { borderColor: 'rgba(74,222,128,0.18)' };
const timelineStepDelayedStyle = { borderColor: 'rgba(255,179,71,0.22)', background: 'rgba(255,179,71,0.07)' };
const timelineStepCriticalStyle = { borderColor: 'rgba(255,95,109,0.24)', background: 'rgba(255,95,109,0.08)' };
const timelineStepPendingStyle = { opacity: 0.55 };
const timelineRowStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid rgba(255,255,255,0.06)' };
const chatBubbleStyle = { borderRadius: 16, background: '#081423', border: '1px solid rgba(255,255,255,0.04)', padding: 14, marginBottom: 12 };
const myChatBubbleStyle = { background: 'rgba(90,200,250,0.08)', border: '1px solid rgba(90,200,250,0.12)' };
const chatComposeStyle = { marginTop: 16, display: 'flex', gap: 10 };
const inputStyle = { flex: 1, background: '#081423', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14, color: '#fff', padding: '13px 14px', minWidth: 0 };
const attachmentStyle = { display: 'block', marginTop: 10, color: '#9defff', textDecoration: 'none' };
const evidenceStyle = { borderRadius: 18, background: '#101d31', border: '1px solid rgba(255,255,255,0.04)', padding: 16, minHeight: 154 };
const proofBoxStyle = { marginTop: 14, height: 74, borderRadius: 12, background: '#081423', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.8 };
const mapPlaceholderStyle = { marginTop: 18, height: 260, borderRadius: 18, background: 'linear-gradient(135deg, rgba(90,200,250,0.12), rgba(0,0,0,0))', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9defff', fontWeight: 800, letterSpacing: '0.06em', textAlign: 'center', padding: 20 };
const errorStyle = { marginTop: 16, borderRadius: 14, padding: 12, background: 'rgba(255,95,109,0.12)', color: '#ffb3bc' };

const modalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 9999,
  background: 'rgba(3,8,18,0.78)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
};

const modalCardStyle = {
  width: 'min(560px, 100%)',
  borderRadius: 24,
  background: '#0b1728',
  border: '1px solid rgba(90,200,250,0.18)',
  padding: 24,
  boxShadow: '0 24px 80px rgba(0,0,0,0.45)',
};

const textareaStyle = {
  width: '100%',
  minHeight: 120,
  resize: 'vertical',
  background: '#081423',
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 16,
  padding: 14,
  outline: 'none',
};

const dangerActionButton = { ...smallActionButton, border: '1px solid rgba(255,95,109,0.24)', background: 'rgba(255,95,109,0.10)', color: '#ffb3bc' };
